import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Filter, X, Maximize2, Minimize2, Info, ChevronRight, Search, AlertCircle, Plus, Download, ListPlus, Eye, Layers, Sparkles, Sun, Target, ArrowDownLeft, ArrowUpRight, Users, CheckCircle2, BookOpen, Battery, BatteryCharging, Snowflake, ThermometerSun, Droplets, LandPlot, RotateCw, Waves, Sprout, House, Building2, ChevronDown, Check, SlidersHorizontal } from 'lucide-react';
import Tooltip from './Tooltip';
import { renderRichText, extractCitations } from './RichText';

import appVersion from './version.json';
import phases from './data/phases.json';
import stakeholderGroups from './data/stakeholderGroups.json';
import stakeholderRelationships from './data/stakeholderRelationships.json';
import crossFunctionalActivities from './data/crossFunctionalActivities.json';
import activityLevelConnections from './data/activityLevelConnections.json';
import workflowData from './data/workflowData.json';
import activityDescriptions from './data/activityDescriptions.json';
import extrapolatedActivities from './data/extrapolatedActivities.json';
import speData from './data/spe.json';
import speOmData from './data/speOm.json';
import speTddData from './data/speTdd.json';
import quasarData from './data/quasar.json';
import t13ExtremeWeatherData from './data/t13ExtremeWeather.json';
import t13KpisData from './data/t13Kpis.json';
import t13BifacialTrackingData from './data/t13BifacialTracking.json';
import t13ClimateData from './data/t13Climate.json';

// Best-practice overlays: one file per guideline, each mirroring spe.json:
// { source, notes (keyed by T13 activity name), activities }. Sources are
// grouped by toggle: the three SolarPower Europe guidelines share the "Add
// BPG" toggle (group 'spe'); the QUASAR EU project has its own toggle
// (group 'quasar'); supplementary IEA PVPS Task 13 reports (beyond the
// T13-36:2026 canon) share the "Add T13 reports" toggle (group 'iea') —
// future Task 13 reports just add another entry with group 'iea'.
const bpgSources = [
  {
    key: 'spe',
    group: 'spe',
    data: speData,
    label: 'EPC BPG v3.0',
    title: 'SolarPower Europe — Engineering, Procurement and Construction Best Practice Guidelines v3.0',
    citationTitle: 'SolarPower Europe EPC Best Practice Guidelines v3.0 — section reference',
    url: 'https://www.solarpowereurope.org/',
  },
  {
    key: 'om',
    group: 'spe',
    data: speOmData,
    label: 'O&M BPG v6.0',
    title: 'SolarPower Europe — Operation & Maintenance Best Practice Guidelines v6.0',
    citationTitle: 'SolarPower Europe O&M Best Practice Guidelines v6.0 — section reference',
    url: 'https://www.solarpowereurope.org/',
  },
  {
    key: 'tdd',
    group: 'spe',
    data: speTddData,
    label: 'TDD BPG v1.0',
    title: 'SolarPower Europe — Technical Due Diligence Best Practice Guidelines for Hybrid Utility Solar PV & BESS v1.0',
    citationTitle: 'SolarPower Europe Technical Due Diligence Best Practice Guidelines v1.0 — phase reference',
    url: 'https://www.solarpowereurope.org/',
  },
  {
    key: 'quasar',
    group: 'quasar',
    data: quasarData,
    label: 'QUASAR D2.2',
    badgeText: 'QUASAR',
    title: 'QUASAR (EU Horizon Europe, GA 101122298) — D2.2 Best Practice Guide PV Plant Decommissioning / Repowering v1.0',
    citationTitle: 'QUASAR D2.2 (Best Practice Guide PV Plant Decommissioning / Repowering) — section reference',
    url: 'https://quasar-project.eu/',
  },
  {
    key: 't13ew',
    group: 'iea',
    data: t13ExtremeWeatherData,
    label: 'T13-33:2025',
    badgeText: 'T13-33',
    title: 'IEA PVPS Task 13 — T13-33:2025 Operational and Economic Impacts of Extreme Weather on PV Power Plants',
    citationTitle: 'IEA PVPS T13-33:2025 (Extreme Weather) — section reference',
    url: 'https://doi.org/10.69766/FFNG4976',
  },
  {
    key: 't13kpi',
    group: 'iea',
    data: t13KpisData,
    label: 'T13-28:2024',
    badgeText: 'T13-28',
    title: 'IEA PVPS Task 13 — T13-28:2024 Best Practice Guidelines for the Use of Economic and Technical KPIs',
    citationTitle: 'IEA PVPS T13-28:2024 (Economic & Technical KPIs) — section reference',
    url: 'https://iea-pvps.org/research-tasks/performance-operation-and-reliability-of-photovoltaic-systems/',
  },
  {
    key: 't13bifi',
    group: 'iea',
    data: t13BifacialTrackingData,
    label: 'T13-26:2024',
    badgeText: 'T13-26',
    title: 'IEA PVPS Task 13 — T13-26:2024 Best Practices for the Optimization of Bifacial Photovoltaic Tracking Systems',
    citationTitle: 'IEA PVPS T13-26:2024 (Bifacial Tracking) — section reference',
    url: 'https://iea-pvps.org/research-tasks/performance-operation-and-reliability-of-photovoltaic-systems/',
  },
  {
    key: 't13clim',
    group: 'iea',
    data: t13ClimateData,
    label: 'T13-32:2025',
    badgeText: 'T13-32',
    title: 'IEA PVPS Task 13 — T13-32:2025 Optimisation of Photovoltaic Systems for Different Climates',
    citationTitle: 'IEA PVPS T13-32:2025 (Climate Optimisation) — section reference',
    url: 'https://doi.org/10.69766/QSYC8858',
  },
];
// Visual identity per toggle group: SPE renders its logo on orange; QUASAR
// renders a compact navy wordmark badge (project branding, no logo asset);
// supplementary Task 13 reports render a slate wordmark with the report number.
const QUASAR_NAVY = '1D4ED8';
const IEA_SLATE = '334155';

// Descriptions for BPG-only activities are colocated with the activity entry
// in each overlay file. Build lookups so getActivityDescription can find them
// and so the modal can attribute a description to the right guideline.
const speActivityDescriptions = Object.fromEntries(
  bpgSources.flatMap(s => (s.data.activities || [])
    .filter(a => a && a.name && a.description)
    .map(a => [a.name, a.description]))
);
const bpgActivitySource = Object.fromEntries(
  bpgSources.flatMap(s => (s.data.activities || [])
    .filter(a => a && a.name)
    .map(a => [a.name, s]))
);

// Technology scope of an activity: 'pv' (default — the T13 canon is PV-only),
// 'bess' (storage-specific), or 'hybrid' (concerns the combined PV+BESS system).
// Only tagged exceptions carry a `tech` field in the data files.
const activityTech = (a) => (typeof a === 'object' && a && a.tech) || 'pv';
// name -> 'bess' | 'hybrid' for the tagged BPG activities (PV is the default).
const bpgActivityTechMap = Object.fromEntries(
  bpgSources.flatMap(s => (s.data.activities || [])
    .filter(a => a && a.name && a.tech)
    .map(a => [a.name, a.tech]))
);
const TECH_META = {
  pv: { label: 'PV', short: 'PV' },
  bess: { label: 'BESS-specific — battery energy storage decision', short: 'BESS' },
  hybrid: { label: 'Hybrid PV+BESS — concerns the combined system', short: 'Hybrid' },
};

// Climate scope (T13-32:2025 climate classes). Activities may carry
// `climate: [...]` when they only apply in some climates (absent = any
// climate); overlay files may also carry `climateNotes` (activity name →
// climate → note) with climate-specific guidance for existing activities.
const CLIMATE_META = {
  'cold-snowy': { short: 'Cold & Snowy', label: 'Cold & Snowy climates (high latitude / high altitude)', Icon: Snowflake, pill: '#7DD3FC', color: '#0284C7', bg: '#F0F9FF', border: '#BAE6FD' },
  'hot-dry': { short: 'Hot & Dry', label: 'Hot & Dry climates (deserts, steppes)', Icon: ThermometerSun, pill: '#FDBA74', color: '#C2410C', bg: '#FFF7ED', border: '#FED7AA' },
  'hot-humid': { short: 'Hot & Humid', label: 'Hot & Humid climates (tropical, subtropical)', Icon: Droplets, pill: '#6EE7B7', color: '#047857', bg: '#ECFDF5', border: '#A7F3D0' },
};
const CLIMATE_KEYS = Object.keys(CLIMATE_META);
const activityClimates = (a) => (typeof a === 'object' && a && Array.isArray(a.climate) ? a.climate : []);
// name -> climates, for every climate-tagged activity in any data file.
const activityClimateMap = Object.fromEntries([
  ...workflowData.flatMap((r) => r.activities),
  ...extrapolatedActivities.flatMap((r) => r.activities),
  ...bpgSources.flatMap((s) => s.data.activities || []),
].filter((a) => activityClimates(a).length).map((a) => [a.name, a.climate]));

// PV application scope, same rule as climate: `application: [...]` on
// activities (absent = any application) and optionally on overlay notes
// (e.g. the tracker-specific T13-26 guidance).
const APPLICATION_META = {
  ground: { short: 'Ground (fixed)', label: 'Ground-mounted, fixed-tilt', Icon: LandPlot, color: '#78350F' },
  tracker: { short: 'Tracker', label: 'Single-axis tracker plants', Icon: RotateCw, color: '#1D4ED8' },
  floating: { short: 'Floating PV', label: 'Floating PV (FPV)', Icon: Waves, color: '#0369A1' },
  agri: { short: 'Agri-PV', label: 'Agrivoltaics (agri-PV)', Icon: Sprout, color: '#15803D' },
  rooftop: { short: 'Rooftop', label: 'Rooftop / building-applied PV (BAPV, C&I)', Icon: House, color: '#7C2D12' },
  bipv: { short: 'BIPV', label: 'Building-integrated PV (BIPV)', Icon: Building2, color: '#6D28D9' },
};
const APPLICATION_KEYS = Object.keys(APPLICATION_META);
const activityApplications = (a) => (typeof a === 'object' && a && Array.isArray(a.application) ? a.application : []);
const activityAppMap = Object.fromEntries([
  ...workflowData.flatMap((r) => r.activities),
  ...extrapolatedActivities.flatMap((r) => r.activities),
  ...bpgSources.flatMap((s) => s.data.activities || []),
].filter((a) => activityApplications(a).length).map((a) => [a.name, a.application]));
// Short display labels for activities whose canonical name is a paragraph.
const activityLabelMap = Object.fromEntries(
  workflowData.flatMap((r) => r.activities).filter((a) => typeof a === 'object' && a.label).map((a) => [a.name, a.label])
);
const displayName = (name) => activityLabelMap[name] || name;

// Workflow graph. Edges come from activityLevelConnections (base) plus the
// `connections` array of each enabled overlay; a record may store an edge on
// either end, so the index is built from the union and made reciprocal (an
// input of B listed on B is also an output of A when you open A). Evidence per
// edge: 't13' = drawn in the T13-36 workflow diagrams, 'report' = documented in
// the cited section of an overlay report, 'inferred' = curated inference.
const nodeKey = (stakeholder, phase, activity) => `${stakeholder}||${phase}||${activity}`;
const EVIDENCE_RANK = { t13: 3, report: 2, inferred: 1 };
const buildEdgeIndex = (overlaySources) => {
  const edges = new Map();
  const add = (from, to, item, src) => {
    const k = `${nodeKey(from.stakeholder, from.phase, from.activity)}>>${nodeKey(to.stakeholder, to.phase, to.activity)}`;
    const evidence = src ? (item.citation ? 'report' : 'inferred') : (item.inferred ? 'inferred' : 't13');
    const ref = src && item.citation ? { label: src.label, citation: item.citation, citationTitle: src.citationTitle } : null;
    let e = edges.get(k);
    if (!e) {
      e = {
        from: { stakeholder: from.stakeholder, phase: from.phase, activity: from.activity },
        to: { stakeholder: to.stakeholder, phase: to.phase, activity: to.activity },
        evidence, flow: null, refs: [],
      };
      edges.set(k, e);
    }
    if (EVIDENCE_RANK[evidence] > EVIDENCE_RANK[e.evidence]) e.evidence = evidence;
    // A report-documented flow label wins over an inferred one.
    if (item.flow && (!e.flow || ref)) e.flow = item.flow;
    if (ref) e.refs.push(ref);
  };
  const addRecords = (records, src) => (records || []).forEach((r) => {
    (r.inputs || []).forEach((i) => add(i, r, i, src));
    (r.outputs || []).forEach((o) => add(r, o, o, src));
  });
  addRecords(activityLevelConnections, null);
  overlaySources.forEach((s) => addRecords(s.data.connections, s));
  const index = new Map();
  const slot = (n) => {
    const k = nodeKey(n.stakeholder, n.phase, n.activity);
    if (!index.has(k)) index.set(k, { inputs: [], outputs: [] });
    return index.get(k);
  };
  edges.forEach((e) => { slot(e.from).outputs.push(e); slot(e.to).inputs.push(e); });
  return { edges: Array.from(edges.values()), index };
};

// Data model notes (the JSON files above are the source of truth):
// - activityLevelConnections uses FEED (Front-End Engineering Design, Project Developer)
//   and BOQ (Bill of Quantities, EPC) terminology in its inputs/outputs.
// - workflowData activities may be plain strings or { name, type, community } objects,
//   where type is 'SOA' (State of the Art) or 'PBSOA' (Progress Beyond SOA). Only
//   activities explicitly labelled in the source PNG diagrams carry this tag.
// - Edge items may carry `flow` (what passes between the two activities) and,
//   in overlay `connections`, `citation` (the overlay report section documenting it).
// - activityLevelConnections entries flagged `inferred: true` and workflowData /
//   activityLevelConnections entries flagged `community: true` are user-contributed
//   and not confirmed by the T13 report. `extrapolated: true` in
//   extrapolatedActivities marks AI-inferred gap-filling.


// Neutral toolbar button: grey outline at rest, navy when active. Colour in
// the toolbar is reserved for the source layers and the project scopes.
const TOOLBAR_NAVY = '#003366';
const ToolbarButton = ({ active = false, onClick, children, title, ariaLabel, ariaPressed, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={ariaLabel}
    aria-pressed={ariaPressed}
    disabled={disabled}
    className={`px-2.5 py-1.5 rounded-md text-sm flex items-center gap-1.5 whitespace-nowrap border transition-colors ${active ? 'text-white' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
    style={active ? { backgroundColor: TOOLBAR_NAVY, borderColor: TOOLBAR_NAVY } : { opacity: disabled ? 0.4 : 1 }}
  >
    {children}
  </button>
);

// Single-choice dropdown (Climate, Application). value null = "Any".
const ChoiceMenu = ({ label, value, options, onChange, anyLabel = 'Any', anyHint, footer }) => {
  const [open, setOpen] = useState(false);
  const cur = options.find((o) => o.key === value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`px-2.5 py-1.5 rounded-md text-sm flex items-center gap-1.5 whitespace-nowrap border transition-colors ${cur ? 'text-white' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
        style={cur ? { backgroundColor: cur.color, borderColor: cur.color } : undefined}
        title={cur ? `${label}: ${cur.label}` : `${label}: any — choose to tailor the matrix`}
      >
        {cur && <cur.Icon size={13} />}
        <span className={cur ? 'opacity-80' : 'text-gray-500'}>{label}:</span>
        <span className={cur ? 'font-semibold' : ''}>{cur ? cur.short : anyLabel}</span>
        <ChevronDown size={13} className="opacity-70" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div role="listbox" aria-label={label} className="absolute left-0 mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-xl border border-gray-200 z-40 py-1">
            {[{ key: null, short: anyLabel, label: anyHint || 'No filter', Icon: null, color: '#6B7280' }, ...options].map((o) => {
              const selected = (value || null) === o.key;
              return (
                <button
                  key={o.key || 'any'}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => { onChange(o.key); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 ${selected ? 'bg-gray-50' : ''}`}
                >
                  <span className="w-5 flex justify-center flex-shrink-0">
                    {o.Icon ? <o.Icon size={15} style={{ color: o.color }} /> : <span className="w-3 h-3 rounded-full border border-gray-400" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-800">{o.short}</span>
                    <span className="block text-[11px] text-gray-500 leading-snug">{o.label}</span>
                  </span>
                  {selected && <Check size={14} className="text-gray-700 flex-shrink-0" />}
                </button>
              );
            })}
            {footer && <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100 leading-snug">{footer}</div>}
          </div>
        </>
      )}
    </div>
  );
};

const SolarPVWorkflow = () => {

  // Flatten stakeholders list with group information
  const stakeholders = Object.entries(stakeholderGroups).flatMap(([groupKey, group]) =>
    group.stakeholders.map(name => ({
      name,
      group: groupKey,
      groupLabel: group.label,
      color: group.color
    }))
  );

  // State variables
  const [filteredStakeholders, setFilteredStakeholders] = useState(stakeholders.map(s => s.name));
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedView, setExpandedView] = useState(false);
  const [viewMode, setViewMode] = useState("timeline"); // timeline, relationships, or connections
  const [highlightedConnections, setHighlightedConnections] = useState(null); // For visual connection highlighting
  const [searchQuery, setSearchQuery] = useState(""); // Search functionality
  const [showLegend, setShowLegend] = useState(false); // Color legend visibility
  const [zoomLevel, setZoomLevel] = useState(1); // Zoom level for timeline view (0.5 to 2)
  const [focusMode, setFocusMode] = useState(false); // Focus mode - enhanced dimming
  const [collapseMode, setCollapseMode] = useState(false); // Collapse mode - show only relevant stakeholders/phases
  const [showSuggestionModal, setShowSuggestionModal] = useState(false); // Suggestion modal visibility
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // Store user suggestions
  const [showExtrapolated, setShowExtrapolated] = useState(false); // Show/hide extrapolated activities
  const [showOnlyCommunity, setShowOnlyCommunity] = useState(false); // Filter to community-submitted activities only
  const [showSPE, setShowSPE] = useState(false); // Overlay SolarPower Europe best-practice additions
  const [showQuasar, setShowQuasar] = useState(false); // Overlay QUASAR (EU Horizon) decommissioning/repowering additions
  const [showIeaReports, setShowIeaReports] = useState(false); // Overlay supplementary IEA PVPS Task 13 reports (beyond the T13-36 canon)
  const [techFilter, setTechFilter] = useState({ pv: true, bess: true, hybrid: true }); // PV / BESS / Hybrid visibility
  const [showInterfaces, setShowInterfaces] = useState(false); // single-stakeholder filter: also show counterpart activities + needs/delivers panel
  const [interfacePanelOpen, setInterfacePanelOpen] = useState(true); // interface summary panel expanded
  const [applicationFilter, setApplicationFilter] = useState(null); // null = any PV application, else an APPLICATION_META key
  const [climateFilter, setClimateFilter] = useState(null); // null = any climate, else a CLIMATE_META key
  // Which overlay toggle-groups are currently on (see bpgSources[].group).
  const overlayOn = { spe: showSPE, quasar: showQuasar, iea: showIeaReports };
  const [suggestionType, setSuggestionType] = useState('activity'); // activity, input, or output
  const [submitState, setSubmitState] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSourcesMenu, setShowSourcesMenu] = useState(false);
  const [detailTab, setDetailTab] = useState('overview'); // side panel tab
  const [playbookStakeholder, setPlaybookStakeholder] = useState('');
  const [playbookIncludeExtrap, setPlaybookIncludeExtrap] = useState(false);
  const [playbookBusy, setPlaybookBusy] = useState(false);
  // Touch-device preview state: which activity is currently being previewed
  // (highlight-only, no modal). Null on desktop after click → modal opens.
  const [previewedActivity, setPreviewedActivity] = useState(null);

  // Reset suggestion form state whenever the modal closes
  useEffect(() => {
    if (!showSuggestionModal) setSubmitState('idle');
  }, [showSuggestionModal]);

  // Keyboard navigation - ESC to close modal or clear highlighting
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Close topmost overlay first
        if (showPrivacyModal) {
          setShowPrivacyModal(false);
        } else if (showSuggestionModal) {
          setShowSuggestionModal(false);
        } else if (showExportMenu) {
          setShowExportMenu(false);
        } else if (selectedActivity) {
          setSelectedActivity(null);
        } else if (highlightedConnections) {
          setHighlightedConnections(null);
          setPreviewedActivity(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedActivity, highlightedConnections, showSuggestionModal, showPrivacyModal, showExportMenu]);

  // Data-export helpers. JSON = full matrix snapshot for downstream apps;
  // CSV = flat one-row-per-activity view for Excel / DD checklists.
  const triggerDownload = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMatrixJSON = () => {
    const payload = {
      schema: 'iea-pvps-task13-digital-decision-matrix',
      version: appVersion.version,
      exportDate: new Date().toISOString().slice(0, 10),
      source: 'https://solardecisions.eu',
      attribution: 'Becquerel Institute · IEA PVPS Task 13 · SUPERNOVA (Horizon Europe GA 101146883)',
      notes: [
        'Activities flagged community:true are practitioner contributions; extrapolated:true marks AI-inferred gap-filling.',
        'Extrapolated activities may carry validatedBy[] with source, sourceType, citation, note, date — these are the promoted ones.',
        'The top-level "spe", "speOm", "speTdd", "quasar", "t13ExtremeWeather", "t13Kpis", "t13BifacialTracking" and "t13Climate" blocks are separate overlays layered on top of the T13-36:2026 canon: SolarPower Europe EPC Best Practice Guidelines v3.0, O&M Best Practice Guidelines v6.0, Technical Due Diligence Best Practice Guidelines for Hybrid Utility Solar PV & BESS v1.0, the QUASAR EU Horizon project D2.2 Best Practice Guide PV Plant Decommissioning / Repowering v1.0, and the supplementary IEA PVPS Task 13 reports T13-33:2025 (Extreme Weather), T13-28:2024 (Economic and Technical KPIs), T13-26:2024 (Bifacial Tracking) and T13-32:2025 (Climate Optimisation). Each block’s notes are addenda keyed by activity name; its activities are new overlay-only activities not present in T13. Community activities in workflowData may also carry validatedBy[] entries citing reports that confirm them.',
        'Workflow edges: activityLevelConnections and each overlay block\'s "connections" array hold records {stakeholder, phase, activity, inputs[], outputs[]}. An edge may be stored on either end; treat every input/output as a directed edge and take the union (the app makes them reciprocal). Edge items may carry "flow" (what information passes), "inferred": true (curated inference, not stated in a source) or "citation" (section of the overlay report that documents the flow). Overlay edges only apply when that overlay is enabled.',
        'Climate: activities may carry "climate": [...] with values "cold-snowy", "hot-dry", "hot-humid" (absent = applies in any climate); overlay blocks may carry "climateNotes" keyed activity name → climate → {text, citation} with climate-specific guidance for existing activities (T13-32:2025 classes).',
        'PV application: activities may carry "application": [...] with values "ground", "tracker", "floating", "agri", "rooftop", "bipv" (absent = any application); overlay notes may carry the same field when their guidance is application-specific. Some long canonical names carry a short display "label".',
        'Activities may carry a "tech" field: "pv" (default when absent — the T13 canon is PV-only), "bess" (storage-specific), or "hybrid" (concerns the combined PV+BESS system).',
      ].join(' '),
      data: {
        phases,
        stakeholderGroups,
        stakeholderRelationships,
        crossFunctionalActivities,
        activityLevelConnections,
        workflowData,
        activityDescriptions,
        extrapolatedActivities,
      },
      spe: speData,
      speOm: speOmData,
      speTdd: speTddData,
      quasar: quasarData,
      t13ExtremeWeather: t13ExtremeWeatherData,
      t13Kpis: t13KpisData,
      t13BifacialTracking: t13BifacialTrackingData,
      t13Climate: t13ClimateData,
    };
    triggerDownload(
      JSON.stringify(payload, null, 2),
      `digital-decision-matrix-v${appVersion.version}.json`,
      'application/json'
    );
    setShowExportMenu(false);
  };

  const downloadMatrixCSV = () => {
    const groupOf = (name) => {
      for (const [key, group] of Object.entries(stakeholderGroups)) {
        if (group.stakeholders.includes(name)) return { key, label: group.label };
      }
      return { key: '', label: '' };
    };
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // One note/citation column pair per overlay (spe = EPC, om, tdd, quasar).
    const bpgNoteCols = (activityName) =>
      bpgSources.flatMap((s) => {
        const n = (s.data.notes || {})[activityName];
        return n ? [n.text || '', n.citation || ''] : ['', ''];
      });
    // climate: activity's own climate tags; climate_notes: all climate-specific
    // guidance for it, as "climate: text" entries separated by " || ".
    const climateCols = (activityName, a) => [
      activityClimates(a).join('; '),
      bpgSources.flatMap((s) => Object.entries((s.data.climateNotes || {})[activityName] || {})
        .map(([c, n]) => `${c}: ${n.text}`)).join(' || '),
      activityApplications(a).join('; '),
    ];
    const header = [
      'stakeholder', 'group_key', 'group_label', 'phase', 'activity', 'type', 'tech',
      'layer', 'community', 'extrapolated', 'validated_by', 'description',
      'spe_note', 'spe_citation', 'om_note', 'om_citation', 'tdd_note', 'tdd_citation',
      'quasar_note', 'quasar_citation', 't13ew_note', 't13ew_citation',
      't13kpi_note', 't13kpi_citation', 't13bifi_note', 't13bifi_citation',
      't13clim_note', 't13clim_citation', 'climate', 'climate_notes', 'application',
    ];
    const rows = [header];
    workflowData.forEach((row) => {
      const g = groupOf(row.stakeholder);
      (row.activities || []).forEach((a) => {
        const name = typeof a === 'string' ? a : a.name;
        const type = typeof a === 'string' ? '' : a.type || '';
        const community = typeof a === 'string' ? false : !!a.community;
        const layer = community ? 't13-community' : 't13';
        const wfValidated = (typeof a === 'object' && a.validatedBy) || [];
        rows.push([
          row.stakeholder, g.key, g.label, row.phase, name, type, activityTech(a),
          layer, community, false, wfValidated.map((v) => v.source).filter(Boolean).join('; '),
          activityDescriptions[name] || '', ...bpgNoteCols(name), ...climateCols(name, a),
        ]);
      });
    });
    extrapolatedActivities.forEach((e) => {
      const stakeholder = e.stakeholder || '';
      const g = groupOf(stakeholder);
      (e.activities || []).forEach((a) => {
        const name = typeof a === 'string' ? a : a.name;
        const validatedBy = (typeof a === 'object' && a.validatedBy) || [];
        const layer = validatedBy.length ? 't13-ai-validated' : 't13-ai-extrapolated';
        const validatedByStr = validatedBy.map((v) => v.source).filter(Boolean).join('; ');
        rows.push([
          stakeholder, g.key, g.label, e.phase || '', name, '', activityTech(a),
          layer, false, true, validatedByStr,
          activityDescriptions[name] || '', ...bpgNoteCols(name), ...climateCols(name, a),
        ]);
      });
    });
    // BPG-only activities, one layer tag per guideline (spe-bpg kept for the
    // original EPC overlay so downstream consumers don't break).
    const bpgLayerKeys = { spe: 'spe-bpg', om: 'spe-om-bpg', tdd: 'spe-tdd-bpg', quasar: 'quasar-d22', t13ew: 'iea-t13-33', t13kpi: 'iea-t13-28', t13bifi: 'iea-t13-26', t13clim: 'iea-t13-32' };
    bpgSources.forEach((s) => {
      (s.data.activities || []).forEach((a) => {
        const stakeholder = a.stakeholder || '';
        const g = groupOf(stakeholder);
        const noteCols = bpgSources.flatMap((s2) =>
          s2.key === s.key ? ['', a.citation || ''] : ['', '']);
        rows.push([
          stakeholder, g.key, g.label, a.phase || '', a.name || '', a.type || '', activityTech(a),
          bpgLayerKeys[s.key] || 'spe-bpg', false, false, '',
          a.description || '', ...noteCols, ...climateCols(a.name, a),
        ]);
      });
    });
    const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');
    triggerDownload('﻿' + csv, `digital-decision-matrix-v${appVersion.version}.csv`, 'text/csv;charset=utf-8');
    setShowExportMenu(false);
  };

  // Edge list of the workflow graph as currently shown (same layer visibility
  // as the matrix), one row per directed link.
  const downloadWorkflowEdgesCSV = () => {
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [[
      'from_stakeholder', 'from_phase', 'from_activity',
      'to_stakeholder', 'to_phase', 'to_activity',
      'flow', 'evidence', 'sources',
    ]];
    workflowGraph.edges
      .filter((e) => isActivityVisible(e.from.stakeholder, e.from.phase, e.from.activity)
        && isActivityVisible(e.to.stakeholder, e.to.phase, e.to.activity))
      .forEach((e) => rows.push([
        e.from.stakeholder, e.from.phase, e.from.activity,
        e.to.stakeholder, e.to.phase, e.to.activity,
        e.flow || '', e.evidence, e.refs.map((r) => `${r.label} ${r.citation}`).join('; '),
      ]));
    const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');
    triggerDownload('﻿' + csv, `digital-decision-matrix-edges-v${appVersion.version}.csv`, 'text/csv;charset=utf-8');
    setShowExportMenu(false);
  };

  // Filter stakeholders by checkbox selection
  const handleStakeholderFilter = (stakeholder) => {
    if (filteredStakeholders.includes(stakeholder)) {
      setFilteredStakeholders(filteredStakeholders.filter(s => s !== stakeholder));
    } else {
      setFilteredStakeholders([...filteredStakeholders, stakeholder]);
    }
  };

  // Select all stakeholders
  const selectAllStakeholders = () => {
    setFilteredStakeholders(stakeholders.map(s => s.name));
  };

  // Focus on stakeholders by group
  const focusOnGroup = (groupKey) => {
    const groupStakeholders = stakeholderGroups[groupKey].stakeholders;
    setFilteredStakeholders(groupStakeholders);
  };

  // Clear all stakeholder selections
  const clearAllStakeholders = () => {
    setFilteredStakeholders([]);
  };

  // Reciprocal workflow graph for the overlays currently switched on.
  const workflowGraph = useMemo(
    () => buildEdgeIndex(bpgSources.filter((s) => overlayOn[s.group])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showSPE, showQuasar, showIeaReports]
  );

  // Is this activity currently drawn in the matrix (layer toggles + tech filter)?
  const isActivityVisible = (stakeholder, phase, activityName) =>
    getActivities(stakeholder, phase).some((a) => (typeof a === 'string' ? a : a.name) === activityName);

  // Edges touching an activity, limited to counterparts that are visible, so
  // the modal and the highlight never point at hidden pills.
  const getVisibleEdges = (activityName, phase, stakeholder) => {
    const entry = workflowGraph.index.get(nodeKey(stakeholder, phase, activityName));
    if (!entry) return { inputs: [], outputs: [] };
    const toItem = (e, other) => ({
      stakeholder: other.stakeholder,
      phase: other.phase,
      activity: other.activity,
      evidence: e.evidence,
      inferred: e.evidence === 'inferred',
      flow: e.flow,
      refs: e.refs,
    });
    return {
      inputs: entry.inputs.filter((e) => isActivityVisible(e.from.stakeholder, e.from.phase, e.from.activity)).map((e) => toItem(e, e.from)),
      outputs: entry.outputs.filter((e) => isActivityVisible(e.to.stakeholder, e.to.phase, e.to.activity)).map((e) => toItem(e, e.to)),
    };
  };

  // Find activity-level connections for visual highlighting
  const findActivityLevelConnections = (activityName, phase, stakeholder) => {
    const { inputs, outputs } = getVisibleEdges(activityName, phase, stakeholder);
    if (!inputs.length && !outputs.length) return null;
    return { stakeholder, phase, activity: activityName, inputs, outputs };
  };

  // All cross-stakeholder links of one stakeholder, across every phase, for
  // the activities currently visible: what it needs (partner → mine) and
  // what it delivers (mine → partner).
  const computeInterfaces = (role) => {
    const needs = [];
    const delivers = [];
    phases.forEach((phase) => {
      getActivities(role, phase).forEach((a) => {
        const name = typeof a === 'string' ? a : a.name;
        const mine = { stakeholder: role, phase, activity: name };
        const { inputs, outputs } = getVisibleEdges(name, phase, role);
        inputs.filter((x) => x.stakeholder !== role).forEach((x) => needs.push({ partner: x, mine, flow: x.flow, evidence: x.evidence, refs: x.refs }));
        outputs.filter((x) => x.stakeholder !== role).forEach((x) => delivers.push({ partner: x, mine, flow: x.flow, evidence: x.evidence, refs: x.refs }));
      });
    });
    const byPartner = (list) => {
      const m = new Map();
      list.forEach((l) => {
        if (!m.has(l.partner.stakeholder)) m.set(l.partner.stakeholder, []);
        m.get(l.partner.stakeholder).push(l);
      });
      return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
    };
    const counterpart = new Map(); // nodeKey -> 'provides' | 'receives'
    needs.forEach((l) => counterpart.set(nodeKey(l.partner.stakeholder, l.partner.phase, l.partner.activity), 'provides'));
    delivers.forEach((l) => {
      const k = nodeKey(l.partner.stakeholder, l.partner.phase, l.partner.activity);
      if (!counterpart.has(k)) counterpart.set(k, 'receives');
    });
    return {
      role,
      needs,
      delivers,
      needsByPartner: byPartner(needs),
      deliversByPartner: byPartner(delivers),
      partners: new Set([...needs, ...delivers].map((l) => l.partner.stakeholder)),
      counterpart,
    };
  };

  // Transitive dependency chain: everything upstream (feeds this activity,
  // directly or indirectly) and downstream (depends on it). Feedback loops
  // (e.g. operational data back to EPC acceptance) are handled by the visited set.
  const traceDependencyChain = (activityName, phase, stakeholder) => {
    const walk = (dir) => {
      const seen = new Set([nodeKey(stakeholder, phase, activityName)]);
      const out = [];
      const queue = [{ stakeholder, phase, activity: activityName }];
      while (queue.length) {
        const n = queue.shift();
        getVisibleEdges(n.activity, n.phase, n.stakeholder)[dir].forEach((m) => {
          const k = nodeKey(m.stakeholder, m.phase, m.activity);
          if (seen.has(k)) return;
          seen.add(k);
          out.push(m);
          queue.push(m);
        });
      }
      return out;
    };
    return { upstream: walk('inputs'), downstream: walk('outputs') };
  };

  const highlightDependencyChain = (activityName, phase, stakeholder) => {
    const { upstream, downstream } = traceDependencyChain(activityName, phase, stakeholder);
    setHighlightedConnections({
      selected: { stakeholder, phase, activity: activityName },
      inputs: upstream,
      outputs: downstream,
      chain: true,
    });
    setSelectedActivity(null);
  };

  // Find cross-functional activity and connections for a given activity
  const findActivityConnections = (activityName, phase, stakeholder) => {
    // First check for activity-level connections
    const activityLevel = findActivityLevelConnections(activityName, phase, stakeholder);

    // Look for cross-functional activities that match
    const crossFunctional = crossFunctionalActivities.find(
      cfa => cfa.phase === phase && (
        cfa.activity.toLowerCase().includes(activityName.toLowerCase()) ||
        activityName.toLowerCase().includes(cfa.activity.toLowerCase())
      )
    );

    if (crossFunctional) {
      return {
        activity: crossFunctional.activity,
        stakeholders: crossFunctional.stakeholders,
        connections: crossFunctional.connections,
        activityLevel: activityLevel
      };
    }

    // Also check if the stakeholder is involved in any cross-functional activity in this phase
    const relatedActivities = crossFunctionalActivities.filter(
      cfa => cfa.phase === phase && cfa.stakeholders.includes(stakeholder)
    );

    if (relatedActivities.length > 0 || activityLevel) {
      return {
        relatedActivities: relatedActivities.map(ra => ({
          activity: ra.activity,
          stakeholders: ra.stakeholders,
          connections: ra.connections
        })),
        activityLevel: activityLevel
      };
    }

    return activityLevel ? { activityLevel } : null;
  };

  // Look up an activity description, trying most-specific keys first:
  //   "Activity | Phase | Stakeholder"  -> stakeholder-specific meaning
  //   "Activity | Phase"                -> phase-specific meaning
  //   "Activity"                        -> generic
  // This lets the same activity carry different meanings across phases
  // AND across stakeholders within the same phase.
  const getActivityDescription = (activity, phase, stakeholder) =>
    (stakeholder && activityDescriptions[`${activity} | ${phase} | ${stakeholder}`]) ||
    activityDescriptions[`${activity} | ${phase}`] ||
    activityDescriptions[activity] ||
    speActivityDescriptions[activity] ||
    null;

  // Handle activity selection
  // Shared: compute and apply the connection highlight without opening the modal.
  const previewActivityConnections = (activity, phase, stakeholder) => {
    const connections = findActivityConnections(activity, phase, stakeholder);
    setHighlightedConnections({
      selected: { stakeholder, phase, activity },
      inputs: (connections && connections.activityLevel && connections.activityLevel.inputs) || [],
      outputs: (connections && connections.activityLevel && connections.activityLevel.outputs) || []
    });
    return connections;
  };

  // Look up validation metadata for a promoted extrapolation or a
  // community-submitted activity confirmed by a report (e.g. T13-33).
  const getValidatedBy = (activity, phase, stakeholder) => {
    const findIn = (rows) => {
      const entry = rows.find(item => item.stakeholder === stakeholder && item.phase === phase);
      if (!entry) return null;
      const act = (entry.activities || []).find(a => (typeof a === 'string' ? a : a.name) === activity);
      if (!act || typeof act === 'string') return null;
      return act.validatedBy && act.validatedBy.length ? act.validatedBy : null;
    };
    return findIn(extrapolatedActivities) || findIn(workflowData);
  };

  // Is this activity a community submission? (drives the validation wording)
  const isCommunityActivity = (activity, phase, stakeholder) => {
    const entry = workflowData.find(item => item.stakeholder === stakeholder && item.phase === phase);
    if (!entry) return false;
    const act = (entry.activities || []).find(a => (typeof a === 'string' ? a : a.name) === activity);
    return !!(act && typeof act === 'object' && act.community);
  };

  // Open the detail modal for a given activity (assumes highlight is set or will be).
  const openActivityDetail = (activity, phase, stakeholder, connections) => {
    const conns = connections || findActivityConnections(activity, phase, stakeholder);
    // Resolve the description AND its provenance separately so the "Source" line
    // below the description can attribute § citations to the right report.
    const t13Desc =
      (stakeholder && activityDescriptions[`${activity} | ${phase} | ${stakeholder}`]) ||
      activityDescriptions[`${activity} | ${phase}`] ||
      activityDescriptions[activity] ||
      null;
    const speDesc = speActivityDescriptions[activity] || null;
    const description = t13Desc || speDesc || "No detailed description available.";
    const descriptionSource = t13Desc ? 'T13' : (speDesc ? 'SPE' : null);
    // Which BPG file the description came from (EPC / O&M / TDD), for the
    // Source line and citation tooltips in the modal.
    const bpgSource = !t13Desc && speDesc ? bpgActivitySource[activity] || null : null;
    if (!selectedActivity) setDetailTab('overview');
    const shownObj = getActivities(stakeholder, phase).find((a) => (typeof a === 'string' ? a : a.name) === activity);
    setSelectedActivity({
      type: shownObj && typeof shownObj === 'object' ? shownObj.type || null : null,
      name: activity,
      phase: phase,
      stakeholder: stakeholder,
      description,
      descriptionSource,
      bpgSource,
      tech: bpgActivityTechMap[activity] || null,
      climates: activityClimateMap[activity] || [],
      applications: activityAppMap[activity] || [],
      connections: conns,
      validatedBy: getValidatedBy(activity, phase, stakeholder),
      isCommunity: isCommunityActivity(activity, phase, stakeholder)
    });
  };

  const handleActivityClick = (activity, phase, stakeholder) => {
    // On touch-only devices (no hover capability), do tap-once-preview /
    // tap-again-details. This lets phone users see the connection highlights
    // ON the matrix before committing to opening the detail modal.
    const touchOnly = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: none)').matches;

    const samePreview = previewedActivity
      && previewedActivity.activity === activity
      && previewedActivity.phase === phase
      && previewedActivity.stakeholder === stakeholder;

    if (touchOnly && !samePreview) {
      // First tap: preview only.
      previewActivityConnections(activity, phase, stakeholder);
      setPreviewedActivity({ activity, phase, stakeholder });
      return;
    }

    // Desktop click, or second tap on the same activity → open modal.
    const conns = previewActivityConnections(activity, phase, stakeholder);
    openActivityDetail(activity, phase, stakeholder, conns);
    setPreviewedActivity(null);
  };

  // Get activities for a specific stakeholder and phase
  const getActivities = (stakeholder, phase) => {
    const filtered = workflowData.filter(
      item => item.stakeholder === stakeholder && item.phase === phase
    );
    let activities = filtered.length > 0 ? filtered[0].activities : [];

    // Extrapolated activities may be strings (pending) or objects with a
    // validatedBy array (promoted). Validated ones are always visible; the
    // showExtrapolated toggle only gates the still-pending purple ones.
    const allExtrapolated = extrapolatedActivities
      .filter(item => item.stakeholder === stakeholder && item.phase === phase)
      .flatMap(item => item.activities.map(act => {
        if (typeof act === 'string') return { name: act, extrapolated: true };
        return { ...act, extrapolated: true };
      }));

    const validated = allExtrapolated.filter(a => a.validatedBy && a.validatedBy.length);
    const pending = allExtrapolated.filter(a => !(a.validatedBy && a.validatedBy.length));

    activities = [...activities, ...validated];
    if (showExtrapolated) {
      activities = [...activities, ...pending];
    }

    // Overlay best-practice additions (SPE EPC/O&M/TDD, QUASAR, T13 reports) whose toggle is on
    if (showSPE || showQuasar || showIeaReports) {
      const overlayActs = bpgSources
        .filter(s => overlayOn[s.group])
        .flatMap(s => (s.data.activities || [])
          .filter(a => a.stakeholder === stakeholder && a.phase === phase)
          .map(a => ({ name: a.name, type: a.type || 'SOA', spe: true, overlayGroup: s.group, bpgLabel: s.label, badgeText: s.badgeText, tech: a.tech, climate: a.climate, application: a.application })));
      activities = [...activities, ...overlayActs];
    }

    // Climate filter: hide activities that only apply to other climates.
    if (climateFilter) {
      activities = activities.filter((a) => {
        const c = activityClimates(a);
        return !c.length || c.includes(climateFilter);
      });
    }

    // Application filter: hide activities specific to other PV applications.
    if (applicationFilter) {
      activities = activities.filter((a) => {
        const ap = activityApplications(a);
        return !ap.length || ap.includes(applicationFilter);
      });
    }

    // Technology filter: untagged activities count as PV (the T13 canon).
    if (!(techFilter.pv && techFilter.bess && techFilter.hybrid)) {
      activities = activities.filter(a => techFilter[activityTech(a)]);
    }

    return activities;
  };

  // An overlay note scoped to some PV applications only shows when no
  // application is selected or the selected one is in scope.
  const noteApplies = (note) => !applicationFilter || !Array.isArray(note.application) || note.application.includes(applicationFilter);

  // Climate-specific notes for an activity from the overlays currently on:
  // [{ source, climate, note }], limited to the selected climate if any.
  const getClimateNotes = (activityName) => bpgSources
    .filter((s) => overlayOn[s.group] && s.data.climateNotes && s.data.climateNotes[activityName])
    .flatMap((s) => Object.entries(s.data.climateNotes[activityName])
      .filter(([c]) => !climateFilter || c === climateFilter)
      .map(([c, note]) => ({ source: s, climate: c, note })));

  // Set the climate from the dropdown (null = any).
  const chooseClimate = (key) => {
    if (key && !showIeaReports) setShowIeaReports(true);
    setClimateFilter(key);
  };

  // Toggle one technology chip. Never allow zero selected; narrowing to
  // storage-only content auto-enables the BPG overlay, since BESS/hybrid
  // activities currently live in the SolarPower Europe layers.
  const toggleTechFilter = (tech) => {
    const next = { ...techFilter, [tech]: !techFilter[tech] };
    if (!next.pv && !next.bess && !next.hybrid) return;
    if (!next.pv && !showSPE) setShowSPE(true);
    setTechFilter(next);
  };

  // Check if an activity should be highlighted and return highlight type
  const getActivityHighlightType = (stakeholder, phase, activityName) => {
    if (!highlightedConnections) {
      // Interface view: counterparts that feed the role show as inputs,
      // counterparts it feeds show as outputs; the role's own pills stay normal.
      if (interfaces && stakeholder !== interfaces.role) {
        const kind = interfaces.counterpart.get(nodeKey(stakeholder, phase, activityName));
        return kind === 'provides' ? 'input' : kind === 'receives' ? 'output' : null;
      }
      return null;
    }

    const { selected, inputs, outputs } = highlightedConnections;

    // Check if this is the selected activity
    if (selected.stakeholder === stakeholder &&
        selected.phase === phase &&
        selected.activity === activityName) {
      return 'selected';
    }

    // Check if this is an input activity
    const isInput = inputs.some(input =>
      input.stakeholder === stakeholder &&
      input.phase === phase &&
      input.activity === activityName
    );
    if (isInput) return 'input';

    // Check if this is an output activity
    const isOutput = outputs.some(output =>
      output.stakeholder === stakeholder &&
      output.phase === phase &&
      output.activity === activityName
    );
    if (isOutput) return 'output';

    return 'dimmed';
  };

  // Determine connection type based on relationship characteristics
  const getConnectionType = (from, to, relationshipType) => {
    // Financial and contractual relationships - solid lines
    if (relationshipType?.includes('Contract') || relationshipType?.includes('Financing')) {
      return 'solid';
    }
    // Data and reporting - dashed lines
    if (relationshipType?.includes('Reporting') || relationshipType?.includes('Data') || relationshipType?.includes('Monitoring')) {
      return 'dashed';
    }
    // Advisory and support - dotted lines
    if (relationshipType?.includes('Advisory') || relationshipType?.includes('Support') || relationshipType?.includes('Consultation')) {
      return 'dotted';
    }
    // Handoff and transfer - double lines
    if (relationshipType?.includes('Handoff') || relationshipType?.includes('Transfer') || relationshipType?.includes('Handover')) {
      return 'double';
    }
    return 'solid'; // default
  };

  // Line weight of a link = strength of its evidence (T13 diagram 3,
  // report-documented 2, inferred 1).
  const getConnectionStrength = (item) => EVIDENCE_RANK[item.evidence] || 1;

  // Get relevant stakeholders based on highlighted connections
  const getRelevantStakeholders = () => {
    if (!highlightedConnections || !collapseMode) {
      return stakeholders.map(s => s.name);
    }

    const { selected, inputs, outputs } = highlightedConnections;
    const relevantStakeholderNames = new Set();

    // Add the selected activity's stakeholder
    relevantStakeholderNames.add(selected.stakeholder);

    // Add all input stakeholders
    inputs.forEach(input => relevantStakeholderNames.add(input.stakeholder));

    // Add all output stakeholders
    outputs.forEach(output => relevantStakeholderNames.add(output.stakeholder));

    return Array.from(relevantStakeholderNames);
  };

  // Get relevant phases based on highlighted connections
  const getRelevantPhases = () => {
    if (!highlightedConnections || !collapseMode) {
      return phases;
    }

    const { selected, inputs, outputs } = highlightedConnections;
    const relevantPhaseNames = new Set();

    // Add the selected activity's phase
    relevantPhaseNames.add(selected.phase);

    // Add all input phases
    inputs.forEach(input => relevantPhaseNames.add(input.phase));

    // Add all output phases
    outputs.forEach(output => relevantPhaseNames.add(output.phase));

    return phases.filter(phase => relevantPhaseNames.has(phase));
  };

  // Search matching function
  const matchesSearch = (activity, phase, stakeholder) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const activityName = typeof activity === 'string' ? activity : activity.name;

    const description = getActivityDescription(activityName, phase, stakeholder);
    return (
      activityName.toLowerCase().includes(query) ||
      phase.toLowerCase().includes(query) ||
      stakeholder.toLowerCase().includes(query) ||
      (description && description.toLowerCase().includes(query))
    );
  };

  // Get activity style based on SOA/PBSOA type
  const getActivityStyle = (activity) => {
    const activityObj = typeof activity === 'string' ? { name: activity, type: undefined } : activity;

    // Light fills with a coloured left edge: the SOA / PBSOA class stays
    // readable while strong colour is reserved for selection and the
    // input / output highlights.
    if (activityObj.type === 'PBSOA') {
      return {
        backgroundColor: '#FBEFF3',
        color: '#5C0E26',
        borderColor: '#EBC4D1',
        borderLeftColor: '#8B1538',  // maroon edge = PBSOA
        borderLeftWidth: '4px',
        fontWeight: '600'
      };
    }
    if (activityObj.type === 'SOA') {
      return {
        backgroundColor: '#EEF4FC',
        color: '#16324F',
        borderColor: '#C7D9EF',
        borderLeftColor: '#4A90E2',  // blue edge = SOA
        borderLeftWidth: '4px',
        fontWeight: '600'
      };
    }
    // Default - no classification
    return {
      backgroundColor: 'white',
      color: '#374151',
      borderColor: '#D1D5DB',
      borderLeftColor: '#9CA3AF',
      borderLeftWidth: '4px',
      fontWeight: '500'
    };
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilteredStakeholders(stakeholders.map(s => s.name));
    setSelectedPhase(null);
    setSelectedActivity(null);
  };

  // Gradient colors for phases
  const getPhaseColor = (index) => {
    const colors = [
      'bg-blue-50', 'bg-blue-100', 'bg-green-50', 'bg-green-100', 
      'bg-yellow-50', 'bg-orange-50', 'bg-red-50', 'bg-purple-50'
    ];
    return colors[index % colors.length];
  };

  // Get stakeholder color based on their group
  const getStakeholderColor = (stakeholderName) => {
    const stakeholder = stakeholders.find(s => s.name === stakeholderName);
    if (!stakeholder) return "border-l-4 border-gray-300";

    const group = Object.values(stakeholderGroups).find(g => g.stakeholders.includes(stakeholderName));
    if (!group) return "border-l-4 border-gray-300";

    return `border-l-4`;
  };

  // Get inline styles for stakeholder based on their group
  const getStakeholderStyle = (stakeholderName) => {
    const group = Object.values(stakeholderGroups).find(g => g.stakeholders.includes(stakeholderName));
    if (!group) return {};

    return {
      borderLeftColor: group.hex,
      borderLeftWidth: '5px',
      backgroundColor: `${group.hex}18` // 18 is hex for ~9% opacity - more visible
    };
  };

  // Get stakeholder badge color based on group (returns className)
  const getStakeholderBadgeColor = (stakeholderName) => {
    return ""; // Will use inline styles instead
  };

  // Get inline badge styles for stakeholder based on their group
  const getStakeholderBadgeStyle = (stakeholderName) => {
    const group = Object.values(stakeholderGroups).find(g => g.stakeholders.includes(stakeholderName));
    if (!group) return { backgroundColor: '#E2E8F0', color: '#4A5568' };

    return {
      backgroundColor: `${group.hex}30`, // 30 is hex for ~18% opacity - more visible
      color: group.hex,
      fontWeight: '600',
      border: `1px solid ${group.hex}60`
    };
  };

  // Get the connection paths between stakeholders
  const renderStakeholderRelationships = () => {
    // Group relationships by phase
    const groupedByPhase = stakeholderRelationships.reduce((acc, rel) => {
      if (!acc[rel.phase]) acc[rel.phase] = [];
      acc[rel.phase].push(rel);
      return acc;
    }, {});

    return (
      <div className="p-4 bg-white rounded-lg border border-gray-300 mt-4">
        <h3 className="font-bold text-gray-700 mb-3">Comprehensive Stakeholder Relationships</h3>
        <p className="text-sm text-gray-600 mb-4">
          Showing {stakeholderRelationships.length} relationships across all project phases
        </p>

        {Object.entries(groupedByPhase).map(([phase, relations]) => (
          <div key={phase} className="mb-6">
            <h4 className="font-semibold text-blue-700 mb-2 flex items-center">
              <span className="bg-blue-100 px-2 py-1 rounded text-sm mr-2">{phase}</span>
              <span className="text-xs text-gray-500">({relations.length} relationships)</span>
            </h4>
            <div className="flex flex-col gap-2">
              {relations.map((relation, index) => (
                <div key={index} className="flex items-center p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100">
                  <div className="px-2 py-1 rounded text-xs" style={getStakeholderBadgeStyle(relation.from)}>
                    {relation.from}
                  </div>
                  <ChevronRight className="mx-2 text-gray-400" size={16} />
                  <div className="px-2 py-1 rounded text-xs" style={getStakeholderBadgeStyle(relation.to)}>
                    {relation.to}
                  </div>
                  <span className="ml-3 text-xs text-gray-700 font-medium">{relation.type}</span>
                  <span className="ml-2 text-xs text-gray-600">- {relation.description}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render a cross-functional activity diagram
  const renderCrossFunctionalActivity = (activity) => {
    return (
      <div key={activity.activity} className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-bold text-blue-700 flex items-center">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2 text-sm">{activity.phase}</span>
            {activity.activity}
          </h4>
          <span className="text-sm text-gray-500">Involves {activity.stakeholders.length} stakeholders</span>
        </div>

        <div className="flex flex-wrap mb-4 gap-2">
          {activity.stakeholders.map(stakeholder => (
            <span
              key={stakeholder}
              className="px-3 py-1 rounded-full text-sm"
              style={getStakeholderBadgeStyle(stakeholder)}
            >
              {stakeholder}
            </span>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <h5 className="font-medium text-gray-700 mb-2">Information Flow</h5>
          <div className="space-y-2">
            {activity.connections.map((connection, idx) => (
              <div key={idx} className="flex items-center p-2 hover:bg-gray-50 rounded">
                <div className="px-2 py-1 rounded text-xs" style={getStakeholderBadgeStyle(connection.from)}>
                  {connection.from}
                </div>
                <ArrowRight className="mx-2 text-gray-400" size={14} />
                <div className="px-2 py-1 rounded text-xs" style={getStakeholderBadgeStyle(connection.to)}>
                  {connection.to}
                </div>
                <span className="ml-3 text-xs text-gray-600">{connection.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Interface view is active when exactly one stakeholder is filtered.
  const interfaceRole = showInterfaces && filteredStakeholders.length === 1 ? filteredStakeholders[0] : null;
  const interfaces = interfaceRole ? computeInterfaces(interfaceRole) : null;

  // Source layers on top of the T13 canon (Sources menu + filter strip).
  const badgePill = (bg, content) => (
    <span className="inline-flex items-center justify-center shadow-sm" style={{ backgroundColor: bg, border: '1.5px solid white', height: 17, padding: '0 5px', borderRadius: 8.5 }}>{content}</span>
  );
  const sourceLayers = [
    {
      key: 'spe', label: 'SolarPower Europe BPGs', color: '#EA580C', on: showSPE, toggle: () => setShowSPE(!showSPE),
      hint: 'EPC v3.0, O&M v6.0 and Technical Due Diligence v1.0 best-practice guidelines',
      badge: badgePill('#EA580C', <img src={`${process.env.PUBLIC_URL}/spe_logo_white_new.01a74b7.svg`} alt="SPE" style={{ height: 10, width: 'auto', display: 'block' }} />),
    },
    {
      key: 'quasar', label: 'QUASAR D2.2', color: `#${QUASAR_NAVY}`, on: showQuasar, toggle: () => setShowQuasar(!showQuasar),
      hint: 'EU Horizon project — decommissioning / repowering best-practice guide',
      badge: badgePill(`#${QUASAR_NAVY}`, <span style={{ color: 'white', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em' }}>QUASAR</span>),
    },
    {
      key: 'iea', label: 'Supplementary Task 13 reports', color: `#${IEA_SLATE}`, on: showIeaReports, toggle: () => setShowIeaReports(!showIeaReports),
      hint: bpgSources.filter((b) => b.group === 'iea').map((b) => b.label).join(' · '),
      badge: badgePill(`#${IEA_SLATE}`, <span style={{ color: 'white', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em' }}>T13-xx</span>),
    },
    {
      key: 'extrap', label: 'AI-extrapolated (awaiting validation)', color: '#9333EA', on: showExtrapolated, toggle: () => setShowExtrapolated(!showExtrapolated),
      hint: 'Gap-filling activities inferred from typical PV workflows; validated ones are always shown',
      badge: <span className="inline-flex items-center justify-center w-7 h-4 rounded border-2 border-dashed" style={{ borderColor: '#9333EA', backgroundColor: '#F3E8FF' }}><Sparkles size={9} style={{ color: '#9333EA' }} /></span>,
    },
    {
      key: 'community', label: 'Only community contributions', color: '#0D9488', on: showOnlyCommunity, toggle: () => setShowOnlyCommunity(!showOnlyCommunity),
      hint: 'Filter the matrix down to practitioner-submitted activities',
      badge: <span className="rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: '#0D9488', border: '1.5px solid white', width: 18, height: 18 }}><Users size={10} style={{ color: 'white' }} /></span>,
    },
  ];

  // Everything currently narrowing or layering the view, as removable chips.
  const activeFilterChips = [];
  if (filteredStakeholders.length < stakeholders.length) {
    activeFilterChips.push({
      key: 'stakeholders',
      label: filteredStakeholders.length === 1 ? `Stakeholder: ${filteredStakeholders[0]}` : `${filteredStakeholders.length} of ${stakeholders.length} stakeholders`,
      clear: () => { setFilteredStakeholders(stakeholders.map((x) => x.name)); setShowInterfaces(false); },
    });
  }
  if (interfaceRole) activeFilterChips.push({ key: 'interfaces', label: `${interfaceRole} interfaces`, color: '#2563EB', clear: () => setShowInterfaces(false) });
  if (selectedPhase) activeFilterChips.push({ key: 'phase', label: `Phase: ${selectedPhase}`, clear: () => setSelectedPhase(null) });
  if (searchQuery) activeFilterChips.push({ key: 'search', label: `Search: “${searchQuery}”`, clear: () => setSearchQuery('') });
  if (!(techFilter.pv && techFilter.bess && techFilter.hybrid)) {
    activeFilterChips.push({ key: 'tech', label: `Technology: ${Object.keys(techFilter).filter((k) => techFilter[k]).map((k) => TECH_META[k].short).join(' + ')}`, color: '#0E7490', clear: () => setTechFilter({ pv: true, bess: true, hybrid: true }) });
  }
  if (applicationFilter) {
    const M = APPLICATION_META[applicationFilter];
    activeFilterChips.push({ key: 'application', label: M.short, Icon: M.Icon, color: M.color, clear: () => setApplicationFilter(null) });
  }
  if (climateFilter) {
    const M = CLIMATE_META[climateFilter];
    activeFilterChips.push({ key: 'climate', label: M.short, Icon: M.Icon, color: M.color, clear: () => setClimateFilter(null) });
  }
  sourceLayers.filter((l) => l.on).forEach((l) => activeFilterChips.push({ key: `src-${l.key}`, label: l.label, color: l.color, dot: true, clear: l.toggle }));
  if (highlightedConnections) {
    activeFilterChips.push({
      key: 'highlight',
      label: `${highlightedConnections.chain ? 'Dependency chain' : 'Highlight'}: ${displayName(highlightedConnections.selected.activity)}`,
      color: '#3B82F6',
      clear: () => { setHighlightedConnections(null); setPreviewedActivity(null); },
    });
    if (collapseMode) activeFilterChips.push({ key: 'collapse', label: 'Collapsed to highlight', clear: () => setCollapseMode(false) });
  }

  const resetAllView = () => {
    setFilteredStakeholders(stakeholders.map((x) => x.name));
    setShowInterfaces(false);
    setSelectedPhase(null);
    setSearchQuery('');
    setTechFilter({ pv: true, bess: true, hybrid: true });
    setApplicationFilter(null);
    setClimateFilter(null);
    setShowSPE(false);
    setShowQuasar(false);
    setShowIeaReports(false);
    setShowExtrapolated(false);
    setShowOnlyCommunity(false);
    setHighlightedConnections(null);
    setPreviewedActivity(null);
    setCollapseMode(false);
    setFocusMode(false);
  };

  // How many activity pills the matrix currently shows (same rules as the grid).
  const countShownActivities = () => {
    const rows = stakeholders
      .filter((x) => filteredStakeholders.includes(x.name) || (interfaces && interfaces.partners.has(x.name)))
      .filter((x) => getRelevantStakeholders().includes(x.name));
    let n = 0;
    rows.forEach((row) => {
      getRelevantPhases().filter((ph) => !selectedPhase || ph === selectedPhase).forEach((ph) => {
        getActivities(row.name, ph).forEach((a) => {
          const name = typeof a === 'string' ? a : a.name;
          if (!matchesSearch(name, ph, row.name)) return;
          if (interfaces && row.name !== interfaces.role && !interfaces.counterpart.has(nodeKey(row.name, ph, name))) return;
          if (showOnlyCommunity && !(typeof a === 'object' && a.community)) return;
          n += 1;
        });
      });
    });
    return n;
  };

  return (
    <div
      className="flex flex-col min-h-dvh md:h-dvh bg-gray-50"
      style={selectedActivity && viewMode === "timeline" && typeof window !== 'undefined' && window.innerWidth >= 1024
        ? { paddingRight: 'min(480px, 100vw)', transition: 'padding-right 0.2s ease-out' }
        : { transition: 'padding-right 0.2s ease-out' }}
    >
      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideInRight {
          from { transform: translateX(24px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes flowRight {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        @keyframes flowPulse {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        @keyframes ripple {
          0% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7),
                        0 0 0 0 rgba(59, 130, 246, 0.7);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(59, 130, 246, 0),
                        0 0 0 20px rgba(59, 130, 246, 0);
          }
          100% {
            box-shadow: 0 0 0 10px rgba(59, 130, 246, 0),
                        0 0 0 20px rgba(59, 130, 246, 0);
          }
        }
        @keyframes rippleGreen {
          0% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
          }
          100% {
            box-shadow: 0 0 0 15px rgba(34, 197, 94, 0);
          }
        }
        @keyframes rippleBlue {
          0% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
          }
          100% {
            box-shadow: 0 0 0 15px rgba(59, 130, 246, 0);
          }
        }
        @keyframes dashedFlow {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: 20;
          }
        }
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
          }
          50% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.6);
          }
        }
        .flow-indicator-input {
          position: relative;
        }
        .flow-indicator-input::before {
          content: '⟹';
          position: absolute;
          top: -12px;
          right: -12px;
          font-size: 20px;
          font-weight: bold;
          color: #22C55E;
          text-shadow: 0 0 8px rgba(34, 197, 94, 0.8);
          animation: bounce 1s ease-in-out infinite;
          z-index: 10;
        }
        .flow-indicator-output {
          position: relative;
        }
        .flow-indicator-output::after {
          content: '⟹';
          position: absolute;
          bottom: -12px;
          right: -12px;
          font-size: 20px;
          font-weight: bold;
          color: #3B82F6;
          text-shadow: 0 0 8px rgba(59, 130, 246, 0.8);
          animation: bounce 1s ease-in-out infinite 0.5s;
          z-index: 10;
        }
        .flow-indicator-selected {
          position: relative;
        }
        .flow-indicator-selected::before {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 6px;
          background: linear-gradient(45deg, transparent 25%, rgba(59, 130, 246, 0.4) 25%, rgba(59, 130, 246, 0.4) 50%, transparent 50%, transparent 75%, rgba(59, 130, 246, 0.4) 75%);
          background-size: 12px 12px;
          animation: dashedFlow 1s linear infinite;
          pointer-events: none;
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
          z-index: -1;
        }
        .flow-indicator-selected::after {
          content: '☀️';
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 18px;
          animation: flowPulse 2s ease-in-out infinite;
          z-index: 10;
          filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.8));
        }
        .connection-strength-1 { border-width: 1px; }
        .connection-strength-2 { border-width: 2px; }
        .connection-strength-3 { border-width: 3px; }
        .connection-dashed {
          border-style: dashed;
        }
        .connection-dotted {
          border-style: dotted;
        }
        .connection-double {
          border-style: double;
          border-width: 4px;
        }
      `}</style>

      {/* Header — three-zone: brand bar, title bar, toolbar */}
      <div className="flex-shrink-0">
        {/* Zone 1: Brand bar — showcases the three logos on white */}
        <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center">
          <div className="flex items-center gap-6 flex-wrap">
            <a
              href="https://solarintelligence.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center transition-opacity hover:opacity-80"
              aria-label="Becquerel Institute"
            >
              <img src="/BI.png" alt="Becquerel Institute" style={{ height: '60px', width: 'auto' }} />
            </a>
            <div className="h-10 w-px bg-gray-300" aria-hidden="true" />
            <a
              href="https://iea-pvps.org/research-tasks/performance-operation-and-reliability-of-photovoltaic-systems/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center transition-opacity hover:opacity-80"
              aria-label="IEA PVPS Task 13"
            >
              <img src="/IEAPVPS.jpeg" alt="IEA PVPS" style={{ height: '48px', width: 'auto' }} />
            </a>
            <div className="h-10 w-px bg-gray-300" aria-hidden="true" />
            <a
              href="https://supernova-pv.eu/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center transition-opacity hover:opacity-80"
              aria-label="SUPERNOVA project"
            >
              <img
                src="/SUPERNOVA_logo_main_neg.svg"
                alt="Supernova"
                style={{ height: '26px', width: 'auto', filter: 'invert(1)' }}
              />
            </a>
          </div>
        </div>

        {/* Zone 2: Title bar — identity of the page */}
        <div className="text-white px-6 py-3" style={{ backgroundColor: '#003366' }}>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight">Solar PV Project Workflow</h1>
                <span
                  title={`Built ${appVersion.buildDate}`}
                  className="text-[10px] font-mono bg-white bg-opacity-15 px-1.5 py-0.5 rounded uppercase tracking-wider"
                >
                  v{appVersion.version}
                </span>
              </div>
              <p className="text-sm opacity-90 mt-0.5">Comprehensive view of all stakeholder interactions across project lifecycle</p>
            </div>
            <div className="flex gap-2 text-xs flex-shrink-0 mt-1 relative">
              <div className="flex items-center gap-1.5 bg-white bg-opacity-10 px-2.5 py-1 rounded">
                <div className="w-3.5 h-3.5 rounded-sm border" style={{ backgroundColor: '#EEF4FC', borderColor: '#C7D9EF', borderLeft: '4px solid #4A90E2' }}></div>
                <span>SOA</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white bg-opacity-10 px-2.5 py-1 rounded">
                <div className="w-3.5 h-3.5 rounded-sm border" style={{ backgroundColor: '#FBEFF3', borderColor: '#EBC4D1', borderLeft: '4px solid #8B1538' }}></div>
                <span>PBSOA</span>
              </div>
              <button
                onClick={() => setShowLegend(!showLegend)}
                className="flex items-center gap-1.5 bg-white bg-opacity-10 hover:bg-opacity-25 px-2.5 py-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-40"
                aria-haspopup="dialog"
                aria-expanded={showLegend}
                title="Explain all symbols, colours and borders used in the matrix"
              >
                <Info size={13} /> Legend
              </button>
              <a
                href="https://solarintelligence.ai/chat?agent=polaris"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white bg-opacity-10 hover:bg-opacity-25 px-2.5 py-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-40"
                title="Chat with POLARIS on solarintelligence.ai about anything in the platform"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/Polaris.svg`}
                  alt=""
                  aria-hidden="true"
                  style={{ height: 22, width: 22, display: 'block' }}
                />
                <span>Ask POLARIS</span>
                <span className="inline-flex items-center bg-white rounded px-1 py-0.5">
                  <img
                    src={`${process.env.PUBLIC_URL}/new_logo.svg`}
                    alt="Solar Intelligence"
                    style={{ height: 22, width: 'auto', display: 'block' }}
                  />
                </span>
              </a>
              {showLegend && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowLegend(false)} aria-hidden="true" />
                  <div
                    role="dialog"
                    aria-label="Matrix legend"
                    className="absolute right-0 top-full mt-2 w-[26rem] max-w-[calc(100vw-2rem)] bg-white text-gray-800 rounded-lg shadow-2xl border border-gray-200 z-50 p-4"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div className="font-semibold text-sm" style={{ color: '#003366' }}>Legend</div>
                      <button
                        onClick={() => setShowLegend(false)}
                        className="text-gray-400 hover:text-gray-700"
                        aria-label="Close legend"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Activity type</div>
                    <div className="grid grid-cols-1 gap-1.5 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border flex-shrink-0" style={{ backgroundColor: '#EEF4FC', borderColor: '#C7D9EF', borderLeft: '4px solid #4A90E2' }} />
                        <span className="text-xs"><span className="font-semibold">SOA</span> — State of the Art</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#FBEFF3', borderColor: '#EBC4D1', borderLeft: '4px solid #8B1538' }}>
                          <Sun size={9} style={{ color: '#F59E0B' }} />
                        </div>
                        <span className="text-xs"><span className="font-semibold">PBSOA</span> — Progress Beyond State of the Art (sun icon on chip)</span>
                      </div>
                    </div>

                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Technology</div>
                    <div className="grid grid-cols-1 gap-1.5 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border flex-shrink-0" style={{ backgroundColor: '#EEF4FC', borderColor: '#C7D9EF', borderLeft: '4px solid #4A90E2' }} />
                        <span className="text-xs"><span className="font-semibold">PV</span> — no icon; the default scope of the Task 13 matrix</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                          <Battery size={10} style={{ color: '#0891B2' }} />
                        </div>
                        <span className="text-xs"><span className="font-semibold">BESS</span> <Battery size={10} className="inline align-[-1px]" style={{ color: '#0891B2' }} /> — battery-storage-specific decision</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                          <BatteryCharging size={10} style={{ color: '#6366F1' }} />
                        </div>
                        <span className="text-xs"><span className="font-semibold">Hybrid</span> <BatteryCharging size={10} className="inline align-[-1px]" style={{ color: '#6366F1' }} /> — concerns the combined PV+BESS system (Project › PV / BESS / Hybrid)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {CLIMATE_KEYS.map((c) => { const M = CLIMATE_META[c]; return <M.Icon key={c} size={11} style={{ color: M.color }} />; })}
                        </div>
                        <span className="text-xs"><span className="font-semibold">Climate</span> — Cold &amp; Snowy / Hot &amp; Dry / Hot &amp; Humid: decision specific to that climate, or (with a climate selected) carrying guidance for it (T13-32; choose in Project › Climate)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {APPLICATION_KEYS.map((k) => { const M = APPLICATION_META[k]; return <M.Icon key={k} size={11} style={{ color: M.color }} />; })}
                        </div>
                        <span className="text-xs"><span className="font-semibold">Application</span> — ground (fixed), tracker, floating, agri-PV, rooftop, BIPV: decision that only applies to those applications (choose in Project › Application)</span>
                      </div>
                    </div>

                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Origin &amp; validation</div>
                    <div className="grid grid-cols-1 gap-1.5 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100" />
                        <span className="text-xs">Solid border — confirmed by IEA PVPS T13-36:2026</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100">
                          <span className="absolute -top-1.5 -left-1.5 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: '#0D9488', border: '1.5px solid white', width: 12, height: 12 }}>
                            <Users size={7} style={{ color: 'white' }} />
                          </span>
                        </div>
                        <span className="text-xs">Community-submitted — pending expert review</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border-2 border-dashed flex-shrink-0" style={{ borderColor: '#9333EA', backgroundColor: 'rgba(147, 51, 234, 0.1)' }} />
                        <span className="text-xs">Dashed purple + <Sparkles size={10} className="inline align-[-1px]" style={{ color: '#9333EA' }} /> — AI-extrapolated, awaiting validation (Sources › AI-extrapolated)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                          <CheckCircle2 size={10} style={{ color: '#16A34A' }} />
                        </div>
                        <span className="text-xs">Green <CheckCircle2 size={10} className="inline align-[-1px]" style={{ color: '#16A34A' }} /> — AI-extrapolated, promoted after external validation (click chip for source)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100">
                          <span
                            className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: '#EA580C', border: '1.5px solid white', height: 12, padding: '0 3px', borderRadius: 6 }}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/spe_logo_white_new.01a74b7.svg`}
                              alt="SPE"
                              style={{ height: 6, width: 'auto', display: 'block' }}
                            />
                          </span>
                        </div>
                        <span className="text-xs">SolarPower Europe BPG addition (Sources › SolarPower Europe BPGs)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100">
                          <span
                            className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: `#${QUASAR_NAVY}`, border: '1.5px solid white', height: 12, padding: '0 3px', borderRadius: 6 }}
                          >
                            <span style={{ color: 'white', fontSize: 6, fontWeight: 700, lineHeight: 1 }}>QUASAR</span>
                          </span>
                        </div>
                        <span className="text-xs">QUASAR EU project addition — decommissioning / repowering (Sources › QUASAR D2.2)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100">
                          <span
                            className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: `#${IEA_SLATE}`, border: '1.5px solid white', height: 12, padding: '0 3px', borderRadius: 6 }}
                          >
                            <span style={{ color: 'white', fontSize: 6, fontWeight: 700, lineHeight: 1 }}>T13-33</span>
                          </span>
                        </div>
                        <span className="text-xs">Supplementary IEA PVPS Task 13 reports — T13-26 bifacial tracking, T13-28 KPIs, T13-32 climates, T13-33 extreme weather (Sources › Supplementary Task 13 reports)</span>
                      </div>
                    </div>

                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Selection &amp; connections</div>
                    <div className="grid grid-cols-1 gap-1.5 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border border-gray-500 flex-shrink-0 bg-gray-100">
                          <span className="absolute -top-1.5 -right-1.5 bg-white rounded-full flex items-center justify-center" style={{ border: '1px solid #3B82F6', width: 12, height: 12 }}>
                            <Target size={7} style={{ color: '#3B82F6' }} />
                          </span>
                        </div>
                        <span className="text-xs">Selected activity</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border flex-shrink-0" style={{ backgroundColor: '#dcfce7', borderColor: '#22C55E' }}>
                          <span className="absolute -top-1.5 -right-1.5 bg-white rounded-full flex items-center justify-center" style={{ border: '1px solid #22C55E', width: 12, height: 12 }}>
                            <ArrowUpRight size={7} style={{ color: '#16A34A' }} />
                          </span>
                        </div>
                        <span className="text-xs">Input — feeds into the selected activity</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative w-4 h-4 rounded-sm border flex-shrink-0" style={{ backgroundColor: '#dbeafe', borderColor: '#3B82F6' }}>
                          <span className="absolute -top-1.5 -right-1.5 bg-white rounded-full flex items-center justify-center" style={{ border: '1px solid #3B82F6', width: 12, height: 12 }}>
                            <ArrowDownLeft size={7} style={{ color: '#2563EB' }} />
                          </span>
                        </div>
                        <span className="text-xs">Output — produced by the selected activity</span>
                      </div>
                    </div>

                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Text hints</div>
                    <div className="grid grid-cols-1 gap-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ borderBottom: '1px dotted #6B7280' }}>PBSOA</span>
                        <span className="text-xs text-gray-700">— dotted underline: hover for acronym definition</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block text-[11px] font-mono px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: '#FEF3E2', color: '#B45309', border: '1px solid #FCD9A8' }}
                        >§4.5</span>
                        <span className="text-xs text-gray-700">— orange chip: report section citation</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Zone 3: Toolbar — grouped controls and primary actions */}
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search activities, phases, stakeholders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 py-2 rounded-md text-sm text-gray-800 bg-white border border-gray-300 w-80 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
            />
            <Search className="absolute left-2.5 top-2.5 text-gray-400" size={16} />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="h-6 w-px bg-gray-300" aria-hidden="true" />

          {/* VIEW — how the matrix is displayed (neutral controls) */}
          <div className="flex items-center gap-1.5" role="group" aria-label="View">
            <ToolbarButton active={showFilters} onClick={() => setShowFilters(!showFilters)} title="Stakeholder, interface and phase filters">
              <Filter size={15} /> Filter
            </ToolbarButton>
            <ToolbarButton
              onClick={() => setViewMode(viewMode === "timeline" ? "relationships" : viewMode === "relationships" ? "connections" : "timeline")}
              title="Switch between the matrix, the stakeholder relationship map and the connections view"
            >
              {viewMode === "timeline" ? "Relationships" : viewMode === "relationships" ? "Connections" : "Matrix"}
            </ToolbarButton>
            <ToolbarButton active={expandedView} onClick={() => setExpandedView(!expandedView)} ariaLabel={expandedView ? 'Normal width' : 'Compact width'} title={expandedView ? 'Normal column width' : 'Compact columns to fit more on screen'}>
              {expandedView ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </ToolbarButton>
            {viewMode === "timeline" && (
              <div className="flex items-center gap-0.5 bg-white border border-gray-300 rounded-md px-1 py-0.5">
                <button onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.1))} className="px-2 rounded hover:bg-gray-100 text-sm text-gray-700" disabled={zoomLevel <= 0.5} style={{ opacity: zoomLevel <= 0.5 ? 0.4 : 1 }} aria-label="Zoom out">−</button>
                <span className="text-xs px-1 min-w-[2.75rem] text-center text-gray-700">{Math.round(zoomLevel * 100)}%</span>
                <button onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.1))} className="px-2 rounded hover:bg-gray-100 text-sm text-gray-700" disabled={zoomLevel >= 2} style={{ opacity: zoomLevel >= 2 ? 0.4 : 1 }} aria-label="Zoom in">+</button>
              </div>
            )}
            {highlightedConnections && viewMode === "timeline" && (
              <>
                <ToolbarButton active={focusMode} onClick={() => setFocusMode(!focusMode)} ariaPressed={focusMode} title="Dim everything not connected to the highlighted activity">
                  <Eye size={14} /> Focus
                </ToolbarButton>
                <ToolbarButton active={collapseMode} onClick={() => setCollapseMode(!collapseMode)} ariaPressed={collapseMode} title="Show only the stakeholders and phases involved in the highlight">
                  <Layers size={14} /> Collapse
                </ToolbarButton>
              </>
            )}
          </div>

          <div className="h-6 w-px bg-gray-300" aria-hidden="true" />

          {/* PROJECT — the project the user is working on */}
          <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Project context">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Project</span>
            <div className="flex items-center rounded-md overflow-hidden border border-gray-300 whitespace-nowrap bg-white" role="group" aria-label="Technology">
              {[
                { key: 'pv', icon: <Sun size={13} />, title: 'Show/hide PV decisions (the Task 13 canon and untagged activities)' },
                { key: 'bess', icon: <Battery size={13} />, title: 'Show/hide BESS-specific decisions (battery energy storage)' },
                { key: 'hybrid', icon: <BatteryCharging size={13} />, title: 'Show/hide hybrid PV+BESS decisions (the combined system)' },
              ].map(({ key, icon, title }) => (
                <button
                  key={key}
                  onClick={() => toggleTechFilter(key)}
                  className={`px-2.5 py-1.5 text-sm flex items-center gap-1 transition-colors border-r last:border-r-0 border-gray-200 ${techFilter[key] ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  style={techFilter[key] ? { backgroundColor: '#0E7490' } : undefined}
                  title={title}
                  aria-pressed={techFilter[key]}
                >
                  {icon} {TECH_META[key].short}
                </button>
              ))}
            </div>
            <ChoiceMenu
              label="Application"
              value={applicationFilter}
              onChange={setApplicationFilter}
              anyLabel="Any"
              anyHint="All PV applications — application-specific decisions are shown and marked"
              options={APPLICATION_KEYS.map((k) => ({ key: k, ...APPLICATION_META[k] }))}
              footer="Hides decisions and guidance that only apply to other applications. Untagged decisions apply to every application."
            />
            <ChoiceMenu
              label="Climate"
              value={climateFilter}
              onChange={chooseClimate}
              anyLabel="Any"
              anyHint="All climates — climate-specific decisions are shown and marked"
              options={CLIMATE_KEYS.map((k) => ({ key: k, ...CLIMATE_META[k] }))}
              footer="Climate classes of IEA PVPS T13-32:2025. Choosing one hides decisions specific to other climates, shows its guidance, and turns on the T13 reports."
            />
          </div>

          <div className="h-6 w-px bg-gray-300" aria-hidden="true" />

          {/* SOURCES — which knowledge layers are shown on top of the T13 canon */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSourcesMenu(!showSourcesMenu)}
              aria-haspopup="menu"
              aria-expanded={showSourcesMenu}
              className="px-2.5 py-1.5 rounded-md text-sm flex items-center gap-1.5 whitespace-nowrap border bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              title="Choose which sources are layered on the Task 13 matrix"
            >
              <SlidersHorizontal size={14} /> Sources
              <span className="flex items-center gap-0.5 ml-0.5">
                {sourceLayers.filter((l) => l.on).map((l) => (
                  <span key={l.key} className="w-2.5 h-2.5 rounded-full border border-white" style={{ backgroundColor: l.color }} />
                ))}
              </span>
              <span className="text-xs text-gray-500">{sourceLayers.filter((l) => l.on).length}/{sourceLayers.length}</span>
              <ChevronDown size={13} className="opacity-70" />
            </button>
            {showSourcesMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowSourcesMenu(false)} aria-hidden="true" />
                <div role="menu" className="absolute left-0 mt-1 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-xl border border-gray-200 z-40 py-1">
                  <div className="px-3 py-2 flex items-center gap-2.5 border-b border-gray-100">
                    <span className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#EEF4FC', border: '1px solid #C7D9EF', borderLeft: '4px solid #4A90E2' }} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800">IEA PVPS T13-36:2026 canon</span>
                      <span className="block text-[11px] text-gray-500">Always shown — the Task 13 decision matrix</span>
                    </span>
                    <Check size={14} className="text-gray-400" />
                  </div>
                  {sourceLayers.map((l) => (
                    <button
                      key={l.key}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={l.on}
                      onClick={l.toggle}
                      className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50"
                    >
                      <span className="flex-shrink-0 w-12 flex justify-center">{l.badge}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800">{l.label}</span>
                        <span className="block text-[11px] text-gray-500 leading-snug">{l.hint}</span>
                      </span>
                      <span
                        className="w-9 h-5 rounded-full relative flex-shrink-0 transition-colors"
                        style={{ backgroundColor: l.on ? l.color : '#D1D5DB' }}
                        aria-hidden="true"
                      >
                        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all" style={{ left: l.on ? 18 : 2 }} />
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Primary actions pushed to the right */}
          <div className="ml-auto relative">
            <button
              onClick={() => setShowExportMenu((v) => !v)}
              className="px-3 py-2 rounded-md text-sm flex items-center gap-1.5 transition-colors whitespace-nowrap text-white"
              style={{ backgroundColor: '#0F766E' }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = '#0B5A52')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = '#0F766E')}
              title="Download the matrix data for use in your organisation"
              aria-haspopup="menu"
              aria-expanded={showExportMenu}
            >
              <Download size={16} /> Export data
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowExportMenu(false)} aria-hidden="true" />
                <div
                  role="menu"
                  className="absolute right-0 mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-xl border border-gray-200 z-40 overflow-hidden"
                >
                  <div className="px-4 py-2 bg-gray-100 text-[10px] uppercase tracking-wider text-gray-600 font-semibold border-b border-gray-200">
                    Whole matrix
                  </div>
                  <button
                    role="menuitem"
                    onClick={downloadMatrixJSON}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-800">JSON snapshot</div>
                    <div className="text-xs text-gray-500 mt-0.5">All 8 data structures bundled with version &amp; attribution. For developers and downstream tools.</div>
                  </button>
                  <button
                    role="menuitem"
                    onClick={downloadMatrixCSV}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-800">CSV (flat activities)</div>
                    <div className="text-xs text-gray-500 mt-0.5">One row per stakeholder &times; phase &times; activity, with descriptions. Opens directly in Excel.</div>
                  </button>
                  <button
                    onClick={downloadWorkflowEdgesCSV}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-800">CSV (workflow edges)</div>
                    <div className="text-xs text-gray-500 mt-0.5">One row per input → output link with what flows and its evidence, for the layers currently shown. Ready for graph tools.</div>
                  </button>

                  <div className="px-4 py-2 bg-gray-100 text-[10px] uppercase tracking-wider text-gray-600 font-semibold border-b border-gray-200">
                    Stakeholder playbook (PDF)
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="text-xs text-gray-600">
                      Phase-by-phase reference for a single stakeholder, with descriptions, SOA / PBSOA flags and citations.
                    </div>
                    <select
                      value={playbookStakeholder}
                      onChange={(e) => setPlaybookStakeholder(e.target.value)}
                      className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                    >
                      <option value="">Select a stakeholder…</option>
                      {Object.entries(stakeholderGroups).map(([gk, g]) => (
                        <optgroup key={gk} label={g.label}>
                          {g.stakeholders.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={playbookIncludeExtrap}
                        onChange={(e) => setPlaybookIncludeExtrap(e.target.checked)}
                      />
                      Include AI-extrapolated activities
                    </label>
                    <button
                      onClick={async () => {
                        if (!playbookStakeholder || playbookBusy) return;
                        setPlaybookBusy(true);
                        try {
                          const mod = await import('./PlaybookPDF');
                          await mod.generatePlaybookPDF(playbookStakeholder, { includeExtrapolated: playbookIncludeExtrap, interfaces: computeInterfaces(playbookStakeholder) });
                          setShowExportMenu(false);
                        } catch (err) {
                          console.error('Playbook generation failed:', err);
                          alert('Sorry — the playbook could not be generated. Please try again.');
                        } finally {
                          setPlaybookBusy(false);
                        }
                      }}
                      disabled={!playbookStakeholder || playbookBusy}
                      className="w-full px-3 py-2 rounded text-sm font-semibold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ backgroundColor: playbookStakeholder ? '#003366' : '#9CA3AF' }}
                      onMouseEnter={(e) => { if (playbookStakeholder && !playbookBusy) e.target.style.backgroundColor = '#002244'; }}
                      onMouseLeave={(e) => { if (playbookStakeholder && !playbookBusy) e.target.style.backgroundColor = '#003366'; }}
                    >
                      {playbookBusy ? 'Generating…' : 'Generate playbook'}
                    </button>
                  </div>

                  <div className="px-4 py-2 bg-gray-50 text-[11px] text-gray-500 border-t border-gray-200">
                    Free for non-commercial reuse with attribution to Becquerel Institute &amp; IEA PVPS Task 13.
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => setShowSuggestionModal(true)}
            className="px-3 py-2 rounded-md text-sm flex items-center gap-1.5 transition-colors whitespace-nowrap text-white"
            style={{ backgroundColor: '#003366' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#002244'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#003366'}
            title="Suggest new activities, inputs, or outputs"
          >
            <Plus size={16} /> Suggest
          </button>
          {suggestions.length > 0 && (
            <button
              onClick={() => {
                const dataStr = JSON.stringify(suggestions, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `workflow-suggestions-${new Date().toISOString().split('T')[0]}.json`;
                link.click();
                URL.revokeObjectURL(url);
              }}
              className="px-2.5 py-2 rounded-md transition-colors whitespace-nowrap relative text-white"
              style={{ backgroundColor: '#22C55E' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#16A34A'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#22C55E'}
              title="Download suggestions"
              aria-label="Download suggestions"
            >
              <Download size={16} />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {suggestions.length}
              </span>
            </button>
          )}
        </div>
        {/* Active filters — what currently narrows or layers the view */}
        {viewMode === "timeline" && (
          <div className="bg-white border-b border-gray-200 px-4 py-1.5 flex items-center gap-1.5 flex-wrap text-xs">
            <span className="text-gray-600 mr-1">
              Showing <span className="font-semibold text-gray-800">{countShownActivities()}</span> activities
              {activeFilterChips.length === 0 && <span className="text-gray-500"> · Task 13 canon, no filters</span>}
            </span>
            {activeFilterChips.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 rounded-full border bg-white"
                style={{ borderColor: c.color || '#CBD5E1', color: '#1F2937' }}
              >
                {c.dot && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />}
                {c.Icon && <c.Icon size={12} style={{ color: c.color }} />}
                {c.label}
                <button type="button" onClick={c.clear} className="rounded-full p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800" aria-label={`Remove ${c.label}`}>
                  <X size={11} />
                </button>
              </span>
            ))}
            {activeFilterChips.length > 0 && (
              <button type="button" onClick={resetAllView} className="ml-1 text-gray-500 underline hover:text-gray-800">Reset all</button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-1 min-h-[70vh] md:min-h-0 overflow-hidden">
        {/* Sidebar filters */}
        {showFilters && (
          <div className="w-64 bg-gray-100 p-4 overflow-y-auto border-r border-gray-300">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold text-gray-700">Filters</h2>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close filter panel"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-1">Stakeholders</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  onClick={selectAllStakeholders}
                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  All
                </button>
                <button
                  onClick={clearAllStakeholders}
                  className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
                >
                  Clear
                </button>
              </div>

              <div className="mb-3">
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Filter by Group:</h4>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(stakeholderGroups).map(([key, group]) => (
                    <button
                      key={key}
                      onClick={() => focusOnGroup(key)}
                      className="text-xs px-2 py-1 rounded transition-colors"
                      style={{
                        backgroundColor: `${group.hex}20`,
                        color: group.hex
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = `${group.hex}30`}
                      onMouseLeave={(e) => e.target.style.backgroundColor = `${group.hex}20`}
                    >
                      {group.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1 max-h-64 overflow-y-auto">
                {Object.entries(stakeholderGroups).map(([groupKey, group]) => (
                  <div key={groupKey}>
                    <h4 className="text-xs font-semibold mt-2 mb-1" style={{ color: group.hex }}>
                      {group.label}
                    </h4>
                    {group.stakeholders.map((stakeholderName) => (
                      <div key={stakeholderName} className="flex items-center ml-2">
                        <input
                          type="checkbox"
                          id={`stakeholder-${stakeholderName}`}
                          checked={filteredStakeholders.includes(stakeholderName)}
                          onChange={() => handleStakeholderFilter(stakeholderName)}
                          className="mr-2"
                        />
                        <label
                          htmlFor={`stakeholder-${stakeholderName}`}
                          className="text-sm text-gray-700"
                        >
                          {stakeholderName}
                        </label>
                        <button
                          type="button"
                          onClick={() => setFilteredStakeholders([stakeholderName])}
                          className="ml-auto mr-1 text-[10px] text-gray-400 hover:text-blue-700 underline"
                          title={`Show only ${stakeholderName}`}
                        >
                          only
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-4 p-2 rounded border" style={{ borderColor: '#BFDBFE', backgroundColor: '#EFF6FF' }}>
              <label className={`flex items-start gap-2 text-sm ${filteredStakeholders.length === 1 ? 'text-gray-800' : 'text-gray-400'}`}>
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={showInterfaces}
                  onChange={() => setShowInterfaces(!showInterfaces)}
                />
                <span>
                  <span className="font-semibold">Show my interfaces</span>
                  <span className="block text-[11px] leading-snug text-gray-600">
                    {filteredStakeholders.length === 1
                      ? `Keeps the activities of other stakeholders that feed ${filteredStakeholders[0]} (green) or that it feeds (blue), with a needs / delivers summary.`
                      : 'Select a single stakeholder (use “only”) to see what it needs from and delivers to the others.'}
                  </span>
                </span>
              </label>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-1">Project Phases</h3>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="phase-all"
                    name="phase"
                    checked={selectedPhase === null}
                    onChange={() => setSelectedPhase(null)}
                    className="mr-2"
                  />
                  <label 
                    htmlFor="phase-all"
                    className="text-sm text-gray-700"
                  >
                    All Phases
                  </label>
                </div>
                {phases.map((phase) => (
                  <div key={phase} className="flex items-center">
                    <input
                      type="radio"
                      id={`phase-${phase}`}
                      name="phase"
                      checked={selectedPhase === phase}
                      onChange={() => setSelectedPhase(phase)}
                      className="mr-2"
                    />
                    <label 
                      htmlFor={`phase-${phase}`}
                      className="text-sm text-gray-700"
                    >
                      {phase}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <button 
              onClick={() => { clearAllFilters(); resetAllView(); }}
              className="w-full bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
            >
              Reset All Filters
            </button>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden p-2 relative">

          {/* Interface view summary — what the single filtered stakeholder needs / delivers */}
          {interfaces && viewMode === "timeline" && (
            <div className="mb-2 rounded-lg border bg-white shadow-sm flex-shrink-0" style={{ borderColor: '#BFDBFE' }}>
              <button
                type="button"
                onClick={() => setInterfacePanelOpen(!interfacePanelOpen)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
              >
                <span className="text-sm font-semibold text-gray-800 flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded text-xs" style={getStakeholderBadgeStyle(interfaces.role)}>{interfaces.role}</span>
                  interfaces
                  <span className="font-normal text-gray-600 text-xs">
                    needs <span className="font-semibold text-green-700">{interfaces.needs.length}</span> inputs from {interfaces.needsByPartner.length} stakeholders
                    {' · '}delivers <span className="font-semibold text-blue-700">{interfaces.delivers.length}</span> outputs to {interfaces.deliversByPartner.length}
                  </span>
                </span>
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  <span className="hidden md:inline">layers shown in the matrix apply</span>
                  <ChevronRight size={14} style={{ transform: interfacePanelOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setShowInterfaces(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShowInterfaces(false); } }}
                    className="p-0.5 rounded hover:bg-gray-100"
                    title="Close the interface view"
                  >
                    <X size={14} />
                  </span>
                </span>
              </button>
              {interfacePanelOpen && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-3 pb-3 overflow-y-auto" style={{ maxHeight: '38vh' }}>
                  {[
                    { title: 'Needs (inputs from others)', groups: interfaces.needsByPartner, isIn: true },
                    { title: 'Delivers (outputs to others)', groups: interfaces.deliversByPartner, isIn: false },
                  ].map(({ title, groups, isIn }) => (
                    <div key={title}>
                      <div className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isIn ? 'text-green-700' : 'text-blue-700'}`}>{title}</div>
                      {groups.length === 0 && <div className="text-xs text-gray-500 italic">None with the current layers.</div>}
                      {groups.map(([partner, links]) => (
                        <details key={partner} className="mb-1.5 group" open={groups.length <= 3}>
                          <summary className="cursor-pointer text-xs flex items-center gap-2 select-none">
                            <span className="px-2 py-0.5 rounded" style={getStakeholderBadgeStyle(partner)}>{partner}</span>
                            <span className="text-gray-500">{links.length}</span>
                          </summary>
                          <ul className="mt-1 ml-2 space-y-1">
                            {links.map((l, i) => (
                              <li key={i}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const conns = previewActivityConnections(l.mine.activity, l.mine.phase, l.mine.stakeholder);
                                    openActivityDetail(l.mine.activity, l.mine.phase, l.mine.stakeholder, conns);
                                  }}
                                  className={`w-full text-left text-[11px] leading-snug rounded px-1.5 py-1 hover:shadow-sm ${isIn ? 'bg-green-50' : 'bg-blue-50'}`}
                                  style={{ borderLeft: `${getConnectionStrength(l) + 1}px solid ${isIn ? '#22C55E' : '#3B82F6'}` }}
                                  title="Open your activity"
                                >
                                  {isIn
                                    ? <><span className="text-gray-600">{l.partner.activity}</span> <span className="text-gray-400">→</span> <span className="font-medium text-gray-800">{l.mine.activity}</span></>
                                    : <><span className="font-medium text-gray-800">{l.mine.activity}</span> <span className="text-gray-400">→</span> <span className="text-gray-600">{l.partner.activity}</span></>}
                                  <span className="text-[10px] text-gray-500"> · {l.mine.phase === l.partner.phase ? l.mine.phase : `${isIn ? l.partner.phase : l.mine.phase} → ${isIn ? l.mine.phase : l.partner.phase}`}</span>
                                  {l.flow && <span className="block italic text-gray-600">↳ {l.flow}</span>}
                                  {l.evidence === 'inferred' && <span className="text-[10px] italic text-teal-700">inferred</span>}
                                  {(l.refs || []).map((r, ri) => (
                                    <span key={ri} className="text-[10px] font-semibold mr-1.5" style={{ color: `#${IEA_SLATE}` }}>{r.label} {r.citation}</span>
                                  ))}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </details>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Color Legend - Shows when activity highlighting is active in timeline view */}
          {highlightedConnections && viewMode === "timeline" && !selectedActivity && (
            <div className="fixed top-20 right-4 z-40 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs max-w-xs">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-semibold text-gray-700">Connection Legend</div>
                <button
                  onClick={() => { setHighlightedConnections(null); setPreviewedActivity(null); }}
                  className="text-gray-400 hover:text-gray-700 transition-colors -mr-1"
                  aria-label="Clear preview"
                  title="Clear preview"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded flex-shrink-0" style={{ border: '2px solid #3B82F6', backgroundColor: '#4A90E2' }}></div>
                  <span className="text-gray-700">Selected activity</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded flex-shrink-0" style={{ borderLeft: '4px solid #16A34A', borderTop: '1px solid #86efac', borderRight: '1px solid #86efac', borderBottom: '1px solid #86efac', backgroundColor: '#dcfce7' }}></div>
                  <span className="text-gray-700">Input (feeds in)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded flex-shrink-0" style={{ borderRight: '4px solid #2563EB', borderTop: '1px solid #93c5fd', borderLeft: '1px solid #93c5fd', borderBottom: '1px solid #93c5fd', backgroundColor: '#dbeafe' }}></div>
                  <span className="text-gray-700">Output (produced)</span>
                </div>
                <div className="border-t border-gray-300 my-2 pt-2">
                  <div className="text-gray-600 font-semibold mb-1">Link evidence (details view):</div>
                  <div className="flex items-center gap-2 ml-2">
                    <div className="w-4 h-1.5 bg-blue-500"></div>
                    <span className="text-gray-600">T13 workflow diagram</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <div className="w-4 h-1 bg-blue-500"></div>
                    <span className="text-gray-600">Cited in an overlay report</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <div className="w-4 h-0.5 bg-blue-500"></div>
                    <span className="text-gray-600">Inferred</span>
                  </div>
                </div>
                {highlightedConnections && highlightedConnections.chain && (
                  <div className="border-t border-gray-300 mt-2 pt-2 text-gray-600">
                    Full dependency chain: green = everything upstream, blue = everything downstream ({highlightedConnections.inputs.length} / {highlightedConnections.outputs.length}). Where a feedback loop makes an activity both, it shows as upstream.
                  </div>
                )}
              </div>
              {previewedActivity && (
                <button
                  onClick={() => {
                    openActivityDetail(previewedActivity.activity, previewedActivity.phase, previewedActivity.stakeholder);
                    setPreviewedActivity(null);
                  }}
                  className="mt-3 w-full px-2 py-1.5 rounded text-xs font-semibold text-white flex items-center justify-center gap-1 transition-colors"
                  style={{ backgroundColor: '#003366' }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#002244')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#003366')}
                >
                  Open details <ChevronRight size={12} />
                </button>
              )}
            </div>
          )}

          {viewMode === "connections" ? (
            <div className="p-4 bg-white rounded-lg border border-gray-300 overflow-auto h-full">
              <h2 className="text-lg font-bold mb-4">Cross-Functional Activity Connections</h2>
              <p className="text-gray-600 mb-6">This view shows how activities involve multiple stakeholders and the information flow between them.</p>
              
              {crossFunctionalActivities.map(activity => renderCrossFunctionalActivity(activity))}
            </div>
          ) : viewMode === "relationships" ? (
            <div className="p-4 bg-white rounded-lg border border-gray-300 overflow-auto h-full">
              <h2 className="text-lg font-bold mb-4">Comprehensive Stakeholder Relationship Map</h2>
              <p className="text-gray-600 mb-6">Explore the complete network of relationships across all stakeholder groups throughout the project lifecycle.</p>

              <div className="flex flex-col gap-6">
                {/* Stakeholder Groups Overview */}
                <div>
                  <h3 className="font-bold text-gray-700 border-b border-gray-200 pb-2 mb-3">Stakeholder Groups</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(stakeholderGroups).map(([key, group]) => (
                      <div
                        key={key}
                        className="p-3 rounded-lg border"
                        style={{
                          backgroundColor: `${group.hex}10`,
                          borderColor: `${group.hex}40`
                        }}
                      >
                        <h4 className="font-semibold text-sm mb-2" style={{ color: group.hex }}>
                          {group.label}
                        </h4>
                        <ul className="text-xs space-y-1">
                          {group.stakeholders.map(s => (
                            <li key={s} className="text-gray-700">• {s}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Detailed Relationships */}
                {renderStakeholderRelationships()}
              </div>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Timeline content container — single scroll container so header and body share width and scroll together horizontally */}
              <div className="flex flex-col flex-1 min-h-0 overflow-auto bg-white rounded-b-lg">
                {/* Phase headers - Timeline View */}
                <div className="flex border-b-3 border-gray-500 bg-white sticky top-0 z-10 shadow-md">
                <div className={`${expandedView ? 'w-24 md:w-32' : 'w-32 md:w-56'} p-2 md:p-4 font-bold text-gray-800 text-sm border-r-3 border-gray-500 bg-white sticky left-0 z-20 flex items-center transition-[width] duration-200`} style={{ backgroundColor: '#f8f9fa', minHeight: '68px', borderRightWidth: '3px' }}>
                  <span>Stakeholder</span>
                </div>
                <div className="flex flex-1">
                  {getRelevantPhases()
                    .filter(phase => !selectedPhase || phase === selectedPhase)
                    .map((phase, index) => (
                      <div
                        key={phase}
                        className={`${getPhaseColor(index)} min-w-32 md:min-w-44 p-2 md:p-4 font-bold text-gray-800 text-xs md:text-sm border-r-2 border-gray-400 flex-1 text-center flex items-center justify-center shadow-sm`}
                        style={{ minHeight: '68px', borderRightWidth: '2px' }}
                      >
                        <span className="leading-tight">{phase}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Stakeholder rows and activities */}
              <div>
                <div className="flex flex-col">
                  {stakeholders
                    .filter(s => filteredStakeholders.includes(s.name) || (interfaces && interfaces.partners.has(s.name)))
                    .filter(s => getRelevantStakeholders().includes(s.name))
                    .map((stakeholder) => (
                      <div
                        key={stakeholder.name}
                        className="flex border-b border-gray-300 hover:bg-gray-50 transition-colors"
                        style={{ minHeight: '80px' }}
                      >
                        <div
                          className={`${expandedView ? 'w-24 md:w-32' : 'w-32 md:w-56'} p-2 md:p-3 font-medium text-gray-700 border-r-2 border-gray-400 sticky left-0 z-10 flex items-center transition-[width] duration-200 ${getStakeholderColor(stakeholder.name)}`}
                          style={{...getStakeholderStyle(stakeholder.name), backgroundColor: `${getStakeholderStyle(stakeholder.name).backgroundColor || '#fff'}`}}
                          title={expandedView ? `${stakeholder.name} — ${stakeholder.groupLabel}` : undefined}
                        >
                          <div className="flex flex-col w-full gap-1 min-w-0">
                            <span className="text-sm font-semibold truncate">{stakeholder.name}</span>
                            {!expandedView && (
                              <span className="text-xs px-2 py-0.5 rounded self-start" style={getStakeholderBadgeStyle(stakeholder.name)}>
                                {stakeholder.groupLabel}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-1">
                          {getRelevantPhases()
                            .filter(phase => !selectedPhase || phase === selectedPhase)
                            .map((phase, index) => {
                              const activities = getActivities(stakeholder.name, phase);
                              return (
                                <div
                                  key={phase}
                                  className={`${getPhaseColor(index)} min-w-32 md:min-w-44 p-2 md:p-3 border-r border-gray-300 flex-1 flex items-start`}
                                >
                                  {/* gap-y is wider than gap-x so the corner badges (SPE/QUASAR/T13
                                      bottom-right, community top-left) dangle into free space
                                      instead of overlapping the neighbouring pill. */}
                                  <div className="flex flex-wrap gap-x-1.5 gap-y-3 content-start w-full">
                                    {activities
                                      .filter(activity => {
                                        const activityName = typeof activity === 'string' ? activity : activity.name;
                                        return matchesSearch(activityName, phase, stakeholder.name);
                                      })
                                      .filter(activity => {
                                        // Interface view: partner rows show only the counterpart activities.
                                        if (!interfaces || stakeholder.name === interfaces.role) return true;
                                        const activityName = typeof activity === 'string' ? activity : activity.name;
                                        return interfaces.counterpart.has(nodeKey(stakeholder.name, phase, activityName));
                                      })
                                      .filter(activity => {
                                        if (!showOnlyCommunity) return true;
                                        const activityObj = typeof activity === 'string' ? {} : activity;
                                        return activityObj.community === true;
                                      })
                                      .map((activity, actIndex) => {
                                      const activityObj = typeof activity === 'string' ? { name: activity, type: 'SOA' } : activity;
                                      const activityStyle = getActivityStyle(activityObj);
                                      const isSelected = selectedActivity && selectedActivity.name === activityObj.name;
                                      const highlightType = getActivityHighlightType(stakeholder.name, phase, activityObj.name);

                                      // Determine styling based on highlight type
                                      let highlightStyle = {};
                                      let ringClass = '';
                                      let connectionClass = '';

                                      if (highlightType === 'selected') {
                                        // Keep the base stakeholder background (so white/dark text stays legible)
                                        // and signal selection only via a thick blue border + soft blue shadow + corner badge.
                                        highlightStyle = {
                                          borderColor: '#3B82F6',
                                          borderWidth: '3px',
                                          boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
                                          position: 'relative',
                                          zIndex: 20,
                                        };
                                      } else if (highlightType === 'input') {
                                        // Input: feeds INTO the selected activity. Flip to a light-green fill + dark-green text
                                        // so it reads visually distinct from the (often blue) base and from output boxes.
                                        highlightStyle = {
                                          backgroundColor: '#dcfce7',
                                          color: '#14532D',
                                          borderLeftColor: '#16A34A',
                                          borderLeftWidth: '4px',
                                          borderTopColor: '#86efac',
                                          borderRightColor: '#86efac',
                                          borderBottomColor: '#86efac',
                                          boxShadow: '0 2px 8px rgba(34, 197, 94, 0.25)',
                                          position: 'relative',
                                          zIndex: 15,
                                        };
                                      } else if (highlightType === 'output') {
                                        // Output: produced BY the selected activity. Light-blue fill + dark-blue text,
                                        // distinct enough from the SOA base blue to read as a different state.
                                        highlightStyle = {
                                          backgroundColor: '#BFDBFE',
                                          color: '#1E3A8A',
                                          borderRightColor: '#2563EB',
                                          borderRightWidth: '4px',
                                          borderTopColor: '#93c5fd',
                                          borderLeftColor: '#93c5fd',
                                          borderLeftWidth: '1px',
                                          borderBottomColor: '#93c5fd',
                                          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                                          position: 'relative',
                                          zIndex: 15,
                                        };
                                      } else if (highlightType === 'dimmed') {
                                        highlightStyle = {
                                          opacity: focusMode ? 0.25 : 0.4,
                                          position: 'relative',
                                          zIndex: 1,
                                        };
                                      }

                                      return (
                                        <div
                                          key={`${activityObj.name}-${actIndex}`}
                                          onClick={() => handleActivityClick(activityObj.name, phase, stakeholder.name)}
                                          className={`
                                            relative px-2 py-1.5 text-xs rounded cursor-pointer transition-all duration-200
                                            ${ringClass}
                                            ${connectionClass}
                                            ${highlightType !== 'dimmed' ? 'hover:shadow-lg hover:-translate-y-0.5 hover:scale-105' : ''}
                                            ${(activityObj.extrapolated && !(activityObj.validatedBy && activityObj.validatedBy.length)) ? 'border-dashed border-2' : 'border'} shadow-sm flex-shrink-0
                                          `}
                                          style={{
                                            ...activityStyle,
                                            ...highlightStyle,
                                            maxWidth: '100%',
                                            wordBreak: 'break-word',
                                            lineHeight: '1.3',
                                            transform: `scale(${zoomLevel})`,
                                            ...((activityObj.extrapolated && !(activityObj.validatedBy && activityObj.validatedBy.length)) ? {
                                              backgroundColor: activityStyle.backgroundColor ? `${activityStyle.backgroundColor}33` : 'rgba(147, 51, 234, 0.1)',
                                              borderColor: activityStyle.borderColor || '#9333ea'
                                            } : {})
                                          }}
                                        >
                                          {highlightType === 'selected' && (
                                            <Tooltip text="Selected activity">
                                              <span className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 flex items-center justify-center" style={{ border: '1.5px solid #3B82F6', width: 18, height: 18 }}>
                                                <Target size={11} style={{ color: '#3B82F6' }} />
                                              </span>
                                            </Tooltip>
                                          )}
                                          {highlightType === 'input' && (
                                            <Tooltip text="Input — feeds into the selected activity">
                                              <span className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 flex items-center justify-center" style={{ border: '1.5px solid #22C55E', width: 18, height: 18 }}>
                                                <ArrowUpRight size={11} style={{ color: '#16A34A' }} />
                                              </span>
                                            </Tooltip>
                                          )}
                                          {highlightType === 'output' && (
                                            <Tooltip text="Output — produced by the selected activity">
                                              <span className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 flex items-center justify-center" style={{ border: '1.5px solid #3B82F6', width: 18, height: 18 }}>
                                                <ArrowDownLeft size={11} style={{ color: '#2563EB' }} />
                                              </span>
                                            </Tooltip>
                                          )}
                                          {activityObj.community && (
                                            <Tooltip text={activityObj.validatedBy && activityObj.validatedBy.length
                                              ? `Submitted by the community — confirmed by ${activityObj.validatedBy.map(v => v.source).join(', ')}`
                                              : 'Submitted by the community — inputs and outputs are inferred, not yet confirmed'}>
                                              <span
                                                className="absolute -top-1.5 -left-1.5 rounded-full flex items-center justify-center shadow-sm"
                                                style={{ backgroundColor: '#0D9488', border: '1.5px solid white', width: 18, height: 18 }}
                                              >
                                                <Users size={10} style={{ color: 'white' }} />
                                              </span>
                                            </Tooltip>
                                          )}
                                          {/* Provenance badges dangle from the bottom-right corner;
                                              the pill container's wider gap-y gives them free space
                                              so they don't overlap the pill below. */}
                                          {activityObj.spe && activityObj.overlayGroup !== 'quasar' && activityObj.overlayGroup !== 'iea' && (
                                            <Tooltip text={`SolarPower Europe ${activityObj.bpgLabel || 'Best Practice Guidelines'} — clearly separated from the Task 13 framework`}>
                                              <span
                                                className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center shadow-sm"
                                                style={{ backgroundColor: '#EA580C', border: '1.5px solid white', height: 17, padding: '0 5px', borderRadius: 8.5 }}
                                              >
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/spe_logo_white_new.01a74b7.svg`}
                                                  alt="SPE"
                                                  style={{ height: 10, width: 'auto', display: 'block' }}
                                                />
                                              </span>
                                            </Tooltip>
                                          )}
                                          {activityObj.overlayGroup === 'quasar' && (
                                            <Tooltip text="QUASAR (EU Horizon Europe) — D2.2 Best Practice Guide PV Plant Decommissioning / Repowering — clearly separated from the Task 13 framework">
                                              <span
                                                className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center shadow-sm"
                                                style={{ backgroundColor: `#${QUASAR_NAVY}`, border: '1.5px solid white', height: 17, padding: '0 5px', borderRadius: 8.5 }}
                                              >
                                                <span style={{ color: 'white', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em', lineHeight: 1 }}>QUASAR</span>
                                              </span>
                                            </Tooltip>
                                          )}
                                          {activityObj.overlayGroup === 'iea' && (
                                            <Tooltip text={`Supplementary IEA PVPS Task 13 report (${activityObj.bpgLabel}) — layered on top of the T13-36:2026 canon`}>
                                              <span
                                                className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center shadow-sm"
                                                style={{ backgroundColor: `#${IEA_SLATE}`, border: '1.5px solid white', height: 17, padding: '0 5px', borderRadius: 8.5 }}
                                              >
                                                <span style={{ color: 'white', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em', lineHeight: 1 }}>{activityObj.badgeText || 'T13'}</span>
                                              </span>
                                            </Tooltip>
                                          )}
                                          {displayName(activityObj.name)}
                                          {activityObj.type === 'PBSOA' && (
                                            <Sun size={12} className="inline-block ml-1 align-[-2px]" style={{ color: '#D97706' }} aria-label="Progress Beyond State of the Art" />
                                          )}
                                          {(() => {
                                            // One scope mark per pill (technology, climate, application); any
                                            // further scopes collapse into "+n" with the full list on hover.
                                            const marks = [];
                                            if (activityObj.tech === 'bess') marks.push({ key: 'bess', Icon: Battery, color: '#0E7490', tip: TECH_META.bess.label });
                                            if (activityObj.tech === 'hybrid') marks.push({ key: 'hybrid', Icon: BatteryCharging, color: '#4F46E5', tip: TECH_META.hybrid.label });
                                            const ownC = activityClimates(activityObj);
                                            const climates = climateFilter
                                              ? ((ownC.includes(climateFilter) || getClimateNotes(activityObj.name).length) ? [climateFilter] : [])
                                              : ownC;
                                            climates.forEach((c) => {
                                              const M = CLIMATE_META[c];
                                              marks.push({ key: c, Icon: M.Icon, color: M.color, tip: ownC.includes(c) ? `Specific to ${M.label}` : `Has ${M.short}-specific guidance (T13-32) — open for details` });
                                            });
                                            const ownA = activityApplications(activityObj);
                                            (applicationFilter ? ownA.filter((k) => k === applicationFilter) : ownA).forEach((k) => {
                                              const M = APPLICATION_META[k];
                                              marks.push({ key: k, Icon: M.Icon, color: M.color, tip: `Applies to ${ownA.map((x) => APPLICATION_META[x].short).join(', ')} only` });
                                            });
                                            if (!marks.length) return null;
                                            const [first, ...rest] = marks;
                                            return (
                                              <Tooltip text={marks.map((m) => m.tip).join(' · ')}>
                                                <span className="inline-flex items-center ml-1 align-[-2px] gap-0.5">
                                                  <first.Icon size={12} style={{ color: first.color }} aria-label={first.tip} />
                                                  {rest.length > 0 && <span className="text-[9px] font-semibold" style={{ color: '#475569' }}>+{rest.length}</span>}
                                                </span>
                                              </Tooltip>
                                            );
                                          })()}
                                          {activityObj.extrapolated && !(activityObj.validatedBy && activityObj.validatedBy.length) && (
                                            <Sparkles
                                              size={11}
                                              className="inline-block ml-1 align-[-2px]"
                                              style={{ color: '#9333EA' }}
                                              aria-label="Extrapolated activity, awaiting validation"
                                            />
                                          )}
                                          {activityObj.validatedBy && activityObj.validatedBy.length > 0 && (
                                            <Tooltip text={`Validated by ${activityObj.validatedBy.map(v => v.source).join(', ')}`}>
                                              <CheckCircle2
                                                size={11}
                                                className="inline-block ml-1 align-[-2px]"
                                                style={{ color: '#16A34A' }}
                                                aria-label="AI-extrapolated activity, promoted after external validation"
                                              />
                                            </Tooltip>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              </div>
            </div>
          )}

          {/* Activity details — right-hand side panel (the matrix stays visible and clickable) */}
          {selectedActivity && viewMode === "timeline" && (() => {
            const io = selectedActivity.connections && selectedActivity.connections.activityLevel;
            const nIn = io && io.inputs ? io.inputs.length : 0;
            const nOut = io && io.outputs ? io.outputs.length : 0;
            const inPartners = io && io.inputs ? new Set(io.inputs.map((x) => x.stakeholder)).size : 0;
            const overlayNoteCount = bpgSources.filter((s) => overlayOn[s.group] && s.data.notes && s.data.notes[selectedActivity.name] && noteApplies(s.data.notes[selectedActivity.name])).length;
            const climateNoteList = getClimateNotes(selectedActivity.name);
            const guidanceCount = overlayNoteCount + climateNoteList.length;
            const links = io ? [...(io.inputs || []), ...(io.outputs || [])] : [];
            const ev = { t13: 0, report: 0, inferred: 0 };
            links.forEach((l) => { ev[l.evidence] = (ev[l.evidence] || 0) + 1; });
            const matchNote = climateFilter ? climateNoteList.find((c) => c.climate === climateFilter) : null;
            const chain = traceDependencyChain(selectedActivity.name, selectedActivity.phase, selectedActivity.stakeholder);
            const tabs = [
              { id: 'overview', label: 'Overview' },
              { id: 'io', label: `Inputs & outputs · ${nIn + nOut}` },
              { id: 'guidance', label: `Guidance · ${guidanceCount}` },
              { id: 'evidence', label: 'Evidence' },
            ];
            return (
            <aside
              className="fixed top-0 right-0 bottom-0 z-50 bg-white border-l border-gray-300 flex flex-col"
              style={{ width: 'min(480px, 100vw)', boxShadow: '-8px 0 24px rgba(15, 23, 42, 0.12)', animation: 'slideInRight 0.2s ease-out' }}
              role="dialog"
              aria-label={`Details: ${selectedActivity.name}`}
            >
              {/* Header */}
              <div className="px-4 pt-3 pb-2 border-b border-gray-200">
                <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                  <span className="truncate">
                    <span className="font-medium" style={{ color: '#003366' }}>{selectedActivity.stakeholder}</span>
                    <span className="mx-1">·</span>
                    <span className="font-medium" style={{ color: '#C2410C' }}>{selectedActivity.phase}</span>
                  </span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <a
                      href={`https://solarintelligence.ai/chat?agent=polaris&message=${encodeURIComponent(
                        `In the IEA PVPS Task 13 Digital Decision Matrix I'm looking at the activity "${selectedActivity.name}" for the "${selectedActivity.stakeholder}" stakeholder during the "${selectedActivity.phase}" phase of a PV project. Can you tell me more about it — what it involves, why it matters, and what to watch out for?`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs hover:bg-slate-50"
                      style={{ borderColor: '#040b59', color: '#040b59' }}
                      title="Open a POLARIS chat on solarintelligence.ai pre-seeded with this activity"
                    >
                      <img src={`${process.env.PUBLIC_URL}/Polaris.svg`} alt="" aria-hidden="true" style={{ height: 16, width: 16, display: 'block' }} />
                      Ask POLARIS
                    </a>
                    <button
                      type="button"
                      onClick={() => setSelectedActivity(null)}
                      className="p-1 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                      aria-label="Close activity details"
                      title="Close (Esc)"
                    >
                      <X size={16} />
                    </button>
                  </span>
                </div>
                <h2 className="text-lg font-bold mt-1.5 leading-snug break-words" style={{ color: '#003366' }}>{selectedActivity.name}</h2>
                <div className="flex items-center flex-wrap gap-y-1 mt-1.5 -ml-2">
                  {selectedActivity.type && (
                    <span
                      className="inline-flex items-center gap-1 ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={selectedActivity.type === 'PBSOA'
                        ? { backgroundColor: '#FBEFF3', color: '#5C0E26', border: '1px solid #EBC4D1', borderLeft: '4px solid #8B1538' }
                        : { backgroundColor: '#EEF4FC', color: '#16324F', border: '1px solid #C7D9EF', borderLeft: '4px solid #4A90E2' }}
                      title={selectedActivity.type === 'PBSOA' ? 'Progress Beyond State of the Art' : 'State of the Art'}
                    >
                      {selectedActivity.type}
                    </span>
                  )}
{(selectedActivity.climates || []).map((c) => {
                        const M = CLIMATE_META[c];
                        return (
                          <span
                            key={c}
                            className="inline-flex items-center gap-1 ml-2 align-[-2px] text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: M.bg, color: M.color, border: `1px solid ${M.border}` }}
                            title={`Specific to ${M.label}`}
                          >
                            <M.Icon size={10} /> {M.short}
                          </span>
                        );
                      })}
                      {(selectedActivity.applications || []).map((k) => {
                        const M = APPLICATION_META[k];
                        return (
                          <span
                            key={k}
                            className="inline-flex items-center gap-1 ml-2 align-[-2px] text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white"
                            style={{ color: M.color, border: `1px solid ${M.color}55` }}
                            title={`Applies to ${M.label}`}
                          >
                            <M.Icon size={10} /> {M.short}
                          </span>
                        );
                      })}
                      {selectedActivity.tech && selectedActivity.tech !== 'pv' && (
                        <span
                          className="inline-flex items-center gap-1 ml-2 align-[-2px] text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={selectedActivity.tech === 'bess'
                            ? { backgroundColor: '#ECFEFF', color: '#0E7490', border: '1px solid #A5F3FC' }
                            : { backgroundColor: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE' }}
                          title={TECH_META[selectedActivity.tech].label}
                        >
                          {selectedActivity.tech === 'bess'
                            ? <Battery size={10} />
                            : <BatteryCharging size={10} />}
                          {TECH_META[selectedActivity.tech].short}
                        </span>
                      )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 px-3 border-b border-gray-200 overflow-x-auto" role="tablist">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={detailTab === t.id}
                    onClick={() => setDetailTab(t.id)}
                    className="px-2.5 pt-2.5 pb-2 text-sm whitespace-nowrap border-b-[3px] transition-colors"
                    style={detailTab === t.id
                      ? { borderColor: '#E89845', color: '#003366', fontWeight: 700 }
                      : { borderColor: 'transparent', color: '#6B7280' }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm">
                {detailTab === 'overview' && (
                  <>
                    <div className="text-gray-800 leading-relaxed break-words">
                      {renderRichText(selectedActivity.description, {
                        source: selectedActivity.descriptionSource,
                        citationTitle: selectedActivity.bpgSource ? selectedActivity.bpgSource.citationTitle : undefined,
                      })}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <button type="button" onClick={() => setDetailTab('io')} className="text-left rounded-lg p-2.5 border hover:shadow-sm" style={{ borderColor: '#BBF7D0', backgroundColor: '#F0FDF4' }}>
                        <div className="text-xl font-bold" style={{ color: '#15803D' }}>{nIn}</div>
                        <div className="text-[11px] text-gray-700 leading-snug">inputs{inPartners ? ` from ${inPartners} stakeholder${inPartners > 1 ? 's' : ''}` : ''}</div>
                      </button>
                      <button type="button" onClick={() => setDetailTab('io')} className="text-left rounded-lg p-2.5 border hover:shadow-sm" style={{ borderColor: '#BFDBFE', backgroundColor: '#EFF6FF' }}>
                        <div className="text-xl font-bold" style={{ color: '#1D4ED8' }}>{nOut}</div>
                        <div className="text-[11px] text-gray-700 leading-snug">{nOut ? 'outputs' : 'outputs recorded'}</div>
                      </button>
                      <button type="button" onClick={() => setDetailTab('guidance')} className="text-left rounded-lg p-2.5 border hover:shadow-sm" style={{ borderColor: '#FED7AA', backgroundColor: '#FFF7ED' }}>
                        <div className="text-xl font-bold" style={{ color: '#C2410C' }}>{guidanceCount}</div>
                        <div className="text-[11px] text-gray-700 leading-snug">guidance blocks{guidanceCount === 0 ? ' — try Sources' : ''}</div>
                      </button>
                    </div>
                    {matchNote && (() => {
                      const M = CLIMATE_META[matchNote.climate];
                      return (
                        <div className="rounded-lg p-3" style={{ backgroundColor: M.bg, border: `1px solid ${M.border}` }}>
                          <div className="text-[11px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: M.color }}>
                            <M.Icon size={12} /> For your project · {M.short} · {matchNote.source.label}
                          </div>
                          <div className="text-gray-800 leading-relaxed line-clamp-4">{matchNote.note.text}</div>
                          <button type="button" onClick={() => setDetailTab('guidance')} className="mt-1.5 text-xs font-semibold" style={{ color: M.color }}>Read all guidance →</button>
                        </div>
                      );
                    })()}
                    {nOut === 0 && (
                      <div className="rounded-lg p-3 text-xs text-gray-600 border border-dashed" style={{ borderColor: '#93C5FD' }}>
                        No outputs recorded yet for the layers shown. What does this decision feed?{' '}
                        <button type="button" onClick={() => setShowSuggestionModal(true)} className="underline font-semibold" style={{ color: '#1D4ED8' }}>Suggest an output</button>
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => highlightDependencyChain(selectedActivity.name, selectedActivity.phase, selectedActivity.stakeholder)}
                        className="flex-1 px-3 py-2 rounded-md text-xs font-semibold text-white flex items-center justify-center gap-1.5 flex-wrap"
                        style={{ backgroundColor: '#003366' }}
                      >
                        <Layers size={13} /> Show dependency chain
                        <span className="font-normal opacity-80">({chain.upstream.length} up · {chain.downstream.length} down)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSuggestionModal(true)}
                        className="px-3 py-2 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-100"
                      >
                        Suggest change
                      </button>
                    </div>
                  </>
                )}

                {detailTab === 'io' && (
                  selectedActivity.connections ? (
                    <div>
                      {io && (nIn > 0 || nOut > 0) && (() => {
                        // Connection flow diagram: inputs → this activity → outputs,
                        // stacked vertically to fit the side panel. Boxes open that activity.
                        // Same activity owned by several stakeholders (e.g. "Quality KPIs
                        // for contracts") collapses into one box with a ×n count.
                        const group = (items) => {
                          const m = new Map();
                          items.forEach((it) => {
                            if (!m.has(it.activity)) m.set(it.activity, []);
                            m.get(it.activity).push(it);
                          });
                          return Array.from(m.values());
                        };
                        const node = (items, isIn) => {
                          const item = items[0];
                          return (
                            <button
                              key={`${item.phase}|${item.activity}`}
                              type="button"
                              onClick={() => {
                                const conns = previewActivityConnections(item.activity, item.phase, item.stakeholder);
                                openActivityDetail(item.activity, item.phase, item.stakeholder, conns);
                              }}
                              className={`text-[11px] font-medium py-1 px-2 rounded max-w-[13rem] truncate hover:shadow-sm ${isIn ? 'bg-green-100 text-green-900' : 'bg-blue-100 text-blue-900'}`}
                              title={`${item.activity} — ${items.map((x) => `${x.stakeholder} · ${x.phase}`).join('; ')}`}
                            >
                              {displayName(item.activity)}{items.length > 1 && <span className="ml-1 font-semibold opacity-70">×{items.length}</span>}
                            </button>
                          );
                        };
                        const inGroups = group(io.inputs || []);
                        const outGroups = group(io.outputs || []);
                        const more = (n) => <span className="text-[11px] text-gray-500 self-center">+{n} more</span>;
                        const arrow = (color) => (
                          <div className="flex justify-center my-1.5"><ArrowRight size={18} style={{ color, transform: 'rotate(90deg)' }} /></div>
                        );
                        return (
                          <div className="mb-4 p-3 rounded-lg border border-gray-200" style={{ background: 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 50%, #EFF6FF 100%)' }}>
                            <div className="text-xs font-semibold text-gray-700 mb-2 text-center">Connection flow</div>
                            {nIn > 0 && (
                              <div className="flex flex-wrap justify-center gap-1.5">
                                {inGroups.slice(0, 6).map((g) => node(g, true))}
                                {inGroups.length > 6 && more(inGroups.length - 6)}
                              </div>
                            )}
                            {nIn > 0 && arrow('#16A34A')}
                            <div className="flex justify-center">
                              <div
                                className="px-3 py-2 bg-white rounded-lg text-center font-semibold text-sm max-w-full break-words"
                                style={{ border: '2px solid #3B82F6', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)', color: '#003366' }}
                              >
                                {displayName(selectedActivity.name)}
                              </div>
                            </div>
                            {nOut > 0 && arrow('#2563EB')}
                            {nOut > 0 && (
                              <div className="flex flex-wrap justify-center gap-1.5">
                                {outGroups.slice(0, 6).map((g) => node(g, false))}
                                {outGroups.length > 6 && more(outGroups.length - 6)}
                              </div>
                            )}
                            {nOut === 0 && (
                              <div className="text-center text-[11px] text-gray-500 mt-2">No outputs recorded yet</div>
                            )}
                          </div>
                        );
                      })()}
                      {io && (
                        <div>
                      <p className="text-[11px] text-gray-500 mb-3">
                        What flows in and out of this decision. Click a row to follow the workflow; line weight shows the evidence (T13 diagram › report-cited › inferred).
                      </p>

                      {['inputs', 'outputs'].map((dir) => {
                        const items = selectedActivity.connections.activityLevel[dir] || [];
                        if (!items.length) return null;
                        const isIn = dir === 'inputs';
                        const accent = isIn ? '#22C55E' : '#3B82F6';
                        const edge = isIn ? 'rgb(187, 247, 208)' : 'rgb(191, 219, 254)';
                        return (
                          <div key={dir} className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-4 h-4 rounded border-2" style={{ borderColor: accent, backgroundColor: isIn ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)' }}></div>
                              <span className="text-sm font-semibold text-gray-700">
                                {isIn ? 'Inputs (feed into this activity)' : 'Outputs (this activity feeds)'} · {items.length}
                              </span>
                            </div>
                            <div className="space-y-2 ml-6">
                              {items.map((item, idx) => {
                                const strength = getConnectionStrength(item);
                                const otherPhase = item.phase !== selectedActivity.phase;
                                const stakeholderBadge = (
                                  <div className="px-2 py-1 rounded text-xs flex-shrink-0" style={getStakeholderBadgeStyle(item.stakeholder)}>
                                    {item.stakeholder}
                                  </div>
                                );
                                return (
                                  <button
                                    type="button"
                                    key={idx}
                                    onClick={() => {
                                      const conns = previewActivityConnections(item.activity, item.phase, item.stakeholder);
                                      openActivityDetail(item.activity, item.phase, item.stakeholder, conns);
                                    }}
                                    className={`w-full text-left flex items-center p-2 rounded overflow-hidden hover:shadow-sm transition-shadow ${isIn ? 'bg-green-50' : 'bg-blue-50'}`}
                                    style={{ borderLeft: `${strength + 1}px solid ${accent}`, borderTop: `1px solid ${edge}`, borderRight: `1px solid ${edge}`, borderBottom: `1px solid ${edge}` }}
                                  >
                                    {isIn && stakeholderBadge}
                                    {isIn && <ArrowRight className="text-green-600 mx-2 flex-shrink-0" size={14} />}
                                    <span className="text-xs text-gray-700 break-words flex-1 min-w-0">
                                      <span className="font-medium">{item.activity}</span>
                                      {otherPhase && <span className="ml-1 text-[10px] text-gray-500">· {item.phase}</span>}
                                      {item.flow && (
                                        <span className="block text-[11px] italic text-gray-600 mt-0.5">↳ {item.flow}</span>
                                      )}
                                      <span className="block mt-0.5">
                                        {item.evidence === 't13' && (
                                          <Tooltip text="Link drawn in the IEA PVPS T13-36:2026 workflow diagrams">
                                            <span className="text-[10px] font-semibold text-slate-600 mr-1.5">T13 diagram</span>
                                          </Tooltip>
                                        )}
                                        {(item.refs || []).map((r, ri) => (
                                          <Tooltip key={ri} text={r.citationTitle}>
                                            <span className="text-[10px] font-semibold mr-1.5" style={{ color: `#${IEA_SLATE}` }}>
                                              {r.label} {r.citation}
                                            </span>
                                          </Tooltip>
                                        ))}
                                        {item.evidence === 'inferred' && (
                                          <Tooltip text="Curated inference from typical project workflows — not stated in a source; suggest a correction if it is wrong">
                                            <span className="text-[10px] italic text-teal-700">inferred</span>
                                          </Tooltip>
                                        )}
                                      </span>
                                    </span>
                                    {!isIn && <ArrowRight className="text-blue-600 mx-2 flex-shrink-0" size={14} />}
                                    {!isIn && stakeholderBadge}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      {(() => {
                        const { upstream, downstream } = traceDependencyChain(selectedActivity.name, selectedActivity.phase, selectedActivity.stakeholder);
                        return (
                          <button
                            type="button"
                            onClick={() => highlightDependencyChain(selectedActivity.name, selectedActivity.phase, selectedActivity.stakeholder)}
                            className="mt-1 w-full px-3 py-2 rounded text-xs font-semibold text-white flex items-center justify-center gap-1.5 flex-wrap transition-colors"
                            style={{ backgroundColor: '#003366' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#002244')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#003366')}
                          >
                            <Layers size={13} />
                            Show full dependency chain on the matrix
                            <span className="font-normal opacity-80">({upstream.length} upstream · {downstream.length} downstream)</span>
                          </button>
                        );
                      })()}
                        </div>
                      )}
                  {selectedActivity.connections.connections && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <ArrowRight size={16} style={{ color: '#E89845' }} />
                        Information Flow & Connections
                      </h4>

                      {/* Show involved stakeholders */}
                      <div className="mb-3">
                        <span className="text-xs font-semibold text-gray-600 mb-2 block">Involved Stakeholders:</span>
                        <div className="flex flex-wrap gap-2">
                          {selectedActivity.connections.stakeholders.map(sh => (
                            <span
                              key={sh}
                              className="px-2 py-1 rounded text-xs"
                              style={getStakeholderBadgeStyle(sh)}
                            >
                              {sh}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Show connections */}
                      <div className="space-y-2">
                        {selectedActivity.connections.connections.map((connection, idx) => (
                          <div key={idx} className="flex items-center p-2 bg-gray-50 rounded border border-gray-200">
                            <div className="px-2 py-1 rounded text-xs" style={getStakeholderBadgeStyle(connection.from)}>
                              {connection.from}
                            </div>
                            <ArrowRight className="mx-2" size={14} style={{ color: '#E89845' }} />
                            <div className="px-2 py-1 rounded text-xs" style={getStakeholderBadgeStyle(connection.to)}>
                              {connection.to}
                            </div>
                            <span className="ml-3 text-xs text-gray-600">{connection.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show related activities if no direct match */}
                  {selectedActivity.connections.relatedActivities && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <ArrowRight size={16} style={{ color: '#E89845' }} />
                        Related Cross-Functional Activities in {selectedActivity.phase}
                      </h4>

                      {selectedActivity.connections.relatedActivities.map((ra, idx) => (
                        <div key={idx} className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                          <div className="font-medium text-gray-800 mb-2">{ra.activity}</div>

                          <div className="mb-2">
                            <span className="text-xs font-semibold text-gray-600 mb-1 block">Involves:</span>
                            <div className="flex flex-wrap gap-1">
                              {ra.stakeholders.map(sh => (
                                <span
                                  key={sh}
                                  className="px-2 py-0.5 rounded text-xs"
                                  style={getStakeholderBadgeStyle(sh)}
                                >
                                  {sh}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="space-y-1">
                            {ra.connections.map((connection, cIdx) => (
                              <div key={cIdx} className="flex items-center text-xs">
                                <div className="px-1.5 py-0.5 rounded" style={getStakeholderBadgeStyle(connection.from)}>
                                  {connection.from}
                                </div>
                                <ArrowRight className="mx-1" size={12} style={{ color: '#E89845' }} />
                                <div className="px-1.5 py-0.5 rounded" style={getStakeholderBadgeStyle(connection.to)}>
                                  {connection.to}
                                </div>
                                <span className="ml-2 text-gray-600">{connection.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 px-4">
                      <AlertCircle size={40} className="text-gray-300 mb-3" />
                      <h4 className="text-base font-semibold text-gray-600 mb-1">No connections documented</h4>
                      <p className="text-sm text-gray-500 text-center">
                        No input or output links for the layers currently shown.{' '}
                        <button type="button" onClick={() => setShowSuggestionModal(true)} className="underline" style={{ color: '#1D4ED8' }}>Suggest one</button>
                      </p>
                    </div>
                  )
                )}

                {detailTab === 'guidance' && (
                  <>
                    {guidanceCount === 0 && (
                      <div className="text-gray-600 text-sm">
                        No additional guidance for this activity with the current sources. Switch on more layers in <span className="font-semibold">Sources</span>, or pick a Climate / Application in <span className="font-semibold">Project</span>.
                      </div>
                    )}
                  {getClimateNotes(selectedActivity.name).map(({ source, climate, note }) => {
                    const M = CLIMATE_META[climate];
                    return (
                      <div
                        key={`${source.key}-${climate}`}
                        className="rounded-lg p-3"
                        style={{ backgroundColor: M.bg, border: `1px solid ${M.border}` }}
                      >
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span
                            className="inline-flex items-center justify-center"
                            style={{ backgroundColor: `#${IEA_SLATE}`, borderRadius: 3, padding: '3px 5px' }}
                          >
                            <span style={{ color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1 }}>{source.badgeText || 'T13'}</span>
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: M.color }}>
                            <M.Icon size={12} /> {M.short} — {source.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-800 leading-relaxed">
                          {renderRichText(note.text, { citationTitle: source.citationTitle })}
                        </div>
                        {note.citation && (
                          <div className="mt-1 text-[11px] text-gray-600 italic">
                            {note.citation}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {bpgSources
                    .filter((s) => overlayOn[s.group] && s.data.notes && s.data.notes[selectedActivity.name] && noteApplies(s.data.notes[selectedActivity.name]))
                    .map((s) => {
                      const note = s.data.notes[selectedActivity.name];
                      // Per-group note styling: SPE orange w/ logo, QUASAR blue
                      // wordmark, supplementary Task 13 reports slate wordmark.
                      const noteStyle = {
                        spe: { bg: '#FFF7ED', border: '#FED7AA', color: '#C2410C', header: `BPG addition — ${s.label}` },
                        quasar: { bg: '#EFF6FF', border: '#BFDBFE', color: `#${QUASAR_NAVY}`, wordmark: 'QUASAR', header: 'EU project addition — QUASAR D2.2' },
                        iea: { bg: '#F8FAFC', border: '#CBD5E1', color: `#${IEA_SLATE}`, wordmark: s.badgeText || 'T13', header: `Task 13 addition — ${s.label}` },
                      }[s.group] || { bg: '#FFF7ED', border: '#FED7AA', color: '#C2410C', header: s.label };
                      return (
                        <div
                          key={s.key}
                          className="rounded-lg p-3"
                          style={{ backgroundColor: noteStyle.bg, border: `1px solid ${noteStyle.border}` }}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            {noteStyle.wordmark ? (
                              <span
                                className="inline-flex items-center justify-center"
                                style={{ backgroundColor: noteStyle.color, borderRadius: 3, padding: '3px 5px' }}
                              >
                                <span style={{ color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1 }}>{noteStyle.wordmark}</span>
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center justify-center"
                                style={{ backgroundColor: '#C2410C', borderRadius: 3, padding: '2px 5px' }}
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/spe_logo_white_new.01a74b7.svg`}
                                  alt="SolarPower Europe"
                                  style={{ height: 10, width: 'auto', display: 'block' }}
                                />
                              </span>
                            )}
                            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: noteStyle.color }}>
                              {noteStyle.header}
                            </span>
                          </div>
                          <div className="text-sm text-gray-800 leading-relaxed">
                            {note.text}
                          </div>
                          {note.citation && (
                            <div className="mt-1 text-[11px] text-gray-600 italic">
                              {note.citation}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {detailTab === 'evidence' && (
                  <>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Where this decision comes from</div>
                      <div className="rounded-lg border border-gray-200 p-3 text-gray-700">
                        {selectedActivity.descriptionSource === 'SPE' && selectedActivity.bpgSource
                          ? <>Added by <span className="font-semibold">{selectedActivity.bpgSource.title}</span>.</>
                          : selectedActivity.isCommunity
                            ? <>Submitted by the Task 13 community (practitioner contribution).</>
                            : <>IEA PVPS Task 13 decision matrix (T13-36:2026), or an extrapolated gap-filling activity.</>}
                  {(() => {
                    const cites = extractCitations(selectedActivity.description);
                    const isSPE = selectedActivity.descriptionSource === 'SPE';
                    // T13 descriptions only get a Source line when they carry §
                    // citations; BPG descriptions always name their guideline
                    // (the TDD BPG cites phases, not § sections).
                    if (!cites.length && !isSPE) return null;
                    const bpg = isSPE ? (selectedActivity.bpgSource || bpgSources[0]) : null;
                    const label = isSPE ? bpg.label : 'IEA PVPS T13-36:2026';
                    const url = isSPE
                      ? bpg.url
                      : 'https://iea-pvps.org/key-topics/t13-pv-project-decisions-2026/';
                    const title = isSPE
                      ? bpg.title
                      : 'IEA PVPS T13-36:2026 — PV Project Decisions';
                    return (
                      <div className="mt-3 pt-2 border-t border-gray-200 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Source</span>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-gray-600 underline hover:text-orange-600 inline-flex items-center gap-1.5"
                          title={title}
                        >
                          {isSPE && bpg.group === 'quasar' && (
                            <span
                              className="inline-flex items-center justify-center"
                              style={{ backgroundColor: `#${QUASAR_NAVY}`, borderRadius: 3, padding: '3px 5px' }}
                            >
                              <span style={{ color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1 }}>QUASAR</span>
                            </span>
                          )}
                          {isSPE && bpg.group === 'iea' && (
                            <span
                              className="inline-flex items-center justify-center"
                              style={{ backgroundColor: `#${IEA_SLATE}`, borderRadius: 3, padding: '3px 5px' }}
                            >
                              <span style={{ color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1 }}>{bpg.badgeText || 'T13'}</span>
                            </span>
                          )}
                          {isSPE && bpg.group !== 'quasar' && bpg.group !== 'iea' && (
                            <span
                              className="inline-flex items-center justify-center"
                              style={{ backgroundColor: '#C2410C', borderRadius: 3, padding: '2px 5px' }}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/spe_logo_white_new.01a74b7.svg`}
                                alt="SolarPower Europe"
                                style={{ height: 10, width: 'auto', display: 'block' }}
                              />
                            </span>
                          )}
                          {label}
                        </a>
                        {cites.map((c) => (
                          <span
                            key={c}
                            className="inline-block text-[11px] font-mono px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: '#FEF3E2', color: '#B45309', border: '1px solid #FCD9A8' }}
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                      </div>
                    </div>
{selectedActivity.validatedBy && selectedActivity.validatedBy.length > 0 && (
                    <div
                      className="rounded-lg p-3"
                      style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <CheckCircle2 size={12} style={{ color: '#16A34A' }} />
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#16A34A' }}>
                          Validated
                        </span>
                      </div>
                      <div className="text-[11px] italic text-gray-600 mb-2">
                        {selectedActivity.isCommunity
                          ? 'Community-submitted activity, confirmed by an external report.'
                          : 'AI-extrapolated activity, promoted after external validation.'}
                      </div>
                      {selectedActivity.validatedBy.map((v, i) => {
                        const isSpeSource = v.source && v.source.startsWith('SPE');
                        return (
                        <div key={i} className="mb-2 last:mb-0">
                          <div className="flex gap-2 flex-wrap items-baseline">
                            <span className="text-sm font-semibold text-gray-800 inline-flex items-center gap-1.5">
                              {isSpeSource && (
                                <span
                                  className="inline-flex items-center justify-center"
                                  style={{ backgroundColor: '#C2410C', borderRadius: 3, padding: '2px 5px' }}
                                >
                                  <img
                                    src={`${process.env.PUBLIC_URL}/spe_logo_white_new.01a74b7.svg`}
                                    alt="SolarPower Europe"
                                    style={{ height: 10, width: 'auto', display: 'block' }}
                                  />
                                </span>
                              )}
                              {isSpeSource ? v.source.replace(/^SPE\s*/, '') : v.source}
                            </span>
                            {v.citation && (
                              <span className="text-[11px] font-mono text-gray-600">{v.citation}</span>
                            )}
                            {v.sourceType && (
                              <span
                                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' }}
                              >
                                {v.sourceType.replace(/-/g, ' ')}
                              </span>
                            )}
                            {v.date && (
                              <span className="text-[10px] text-gray-500">{v.date}</span>
                            )}
                          </div>
                          {v.note && (
                            <div className="text-[13px] text-gray-700 mt-1 leading-relaxed">{v.note}</div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                    {links.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">How well its links are backed</div>
                        <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                          {ev.t13 > 0 && <span style={{ width: `${(ev.t13 / links.length) * 100}%`, backgroundColor: '#334155' }} />}
                          {ev.report > 0 && <span style={{ width: `${(ev.report / links.length) * 100}%`, backgroundColor: '#64748B' }} />}
                          {ev.inferred > 0 && <span style={{ width: `${(ev.inferred / links.length) * 100}%`, backgroundColor: '#99F6E4' }} />}
                        </div>
                        <div className="flex gap-3 flex-wrap text-[11px] text-gray-600 mt-1.5">
                          <span><span style={{ color: '#334155' }}>■</span> {ev.t13} T13 diagram</span>
                          <span><span style={{ color: '#64748B' }}>■</span> {ev.report} report-cited</span>
                          <span><span style={{ color: '#2DD4BF' }}>■</span> {ev.inferred} inferred</span>
                        </div>
                        {ev.inferred > 0 && (
                          <div className="mt-3 rounded-lg p-3 text-xs text-gray-700" style={{ backgroundColor: '#F0FDFA', border: '1px solid #99F6E4' }}>
                            <span className="font-semibold" style={{ color: '#0F766E' }}>{ev.inferred} inferred link{ev.inferred > 1 ? 's' : ''}</span> are curated inferences not yet stated in a source. Know better?{' '}
                            <button type="button" onClick={() => setShowSuggestionModal(true)} className="underline font-semibold" style={{ color: '#0F766E' }}>Confirm or correct them</button>
                          </div>
                        )}
                      </div>
                    )}
                    {guidanceCount > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Guidance sources</div>
                        <ul className="space-y-1 text-xs text-gray-700">
                          {bpgSources
                            .filter((s) => overlayOn[s.group] && ((s.data.notes && s.data.notes[selectedActivity.name] && noteApplies(s.data.notes[selectedActivity.name])) || climateNoteList.some((c) => c.source.key === s.key)))
                            .map((s) => (
                              <li key={s.key}><a href={s.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-700">{s.title}</a></li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </aside>
            );
          })()}

          {/* Suggestion Modal */}
          {showSuggestionModal && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setShowSuggestionModal(false)}
            >
              <div
                className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4 p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2" style={{ color: '#003366' }}>
                    <Plus size={20} /> Suggest Workflow Improvement
                  </h3>
                  <button
                    onClick={() => setShowSuggestionModal(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                    aria-label="Close suggestion form"
                  >
                    <X size={24} />
                  </button>
                </div>

                {submitState === 'success' ? (
                  <div className="py-8 text-center">
                    <div className="flex justify-center mb-4">
                      <CheckCircle2 size={72} style={{ color: '#117A65' }} />
                    </div>
                    <h4 className="text-2xl font-bold mb-2" style={{ color: '#003366' }}>Thank you!</h4>
                    <p className="text-base text-gray-700 mb-2">
                      Your suggestion has been received.
                    </p>
                    <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
                      We'll review it and add it to the matrix as soon as possible. Validated contributions appear flagged as community-submitted until they are confirmed.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowSuggestionModal(false)}
                      className="px-6 py-2 rounded text-white transition-colors"
                      style={{ backgroundColor: '#003366' }}
                      onMouseEnter={(e) => (e.target.style.backgroundColor = '#002244')}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = '#003366')}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                <>
                <p className="text-sm text-gray-600 mb-4">
                  Help improve this workflow by suggesting new activities, inputs, or outputs. Your suggestions will be collected and can be downloaded for review.
                </p>

                {submitState === 'error' && (
                  <div className="mb-4 p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800 flex items-start gap-2">
                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                    <span>Sorry — your suggestion could not be submitted. Please check your connection and try again.</span>
                  </div>
                )}

                {/* Suggestion Type Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">What would you like to suggest?</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSuggestionType('activity')}
                      className={`flex-1 px-4 py-2 rounded border-2 transition-all ${
                        suggestionType === 'activity'
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      New Activity
                    </button>
                    <button
                      onClick={() => setSuggestionType('input')}
                      className={`flex-1 px-4 py-2 rounded border-2 transition-all ${
                        suggestionType === 'input'
                          ? 'border-green-500 bg-green-50 text-green-700 font-semibold'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      New Input Connection
                    </button>
                    <button
                      onClick={() => setSuggestionType('output')}
                      className={`flex-1 px-4 py-2 rounded border-2 transition-all ${
                        suggestionType === 'output'
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-semibold'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      New Output Connection
                    </button>
                  </div>
                </div>

                {/* Dynamic Form Based on Suggestion Type */}
                <form
                  name="suggestion"
                  method="POST"
                  data-netlify="true"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (submitState === 'submitting') return;
                    const form = e.target;
                    const formData = new FormData(form);
                    formData.set('form-name', 'suggestion');
                    formData.set('suggestionType', suggestionType);
                    setSubmitState('submitting');
                    fetch('/', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(formData).toString()
                    })
                      .then((res) => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        form.reset();
                        setSubmitState('success');
                      })
                      .catch((err) => {
                        console.error('Form submission error:', err);
                        setSubmitState('error');
                      });
                  }}
                  className="space-y-4"
                >
                  <input type="hidden" name="form-name" value="suggestion" />
                  <input type="hidden" name="suggestionType" value={suggestionType} />
                  {suggestionType === 'activity' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Activity Name *</label>
                        <input
                          type="text"
                          name="activityName"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Performance benchmarking"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Stakeholder *</label>
                        <select
                          name="stakeholder"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select stakeholder...</option>
                          {stakeholders.map(s => (
                            <option key={s.name} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Phase *</label>
                        <select
                          name="phase"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select phase...</option>
                          {phases.map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Description *</label>
                        <textarea
                          name="description"
                          required
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Describe what this activity involves and why it's important..."
                        />
                      </div>
                    </>
                  )}

                  {(suggestionType === 'input' || suggestionType === 'output') && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {suggestionType === 'input' ? 'Target Activity (receives input)' : 'Source Activity (produces output)'} *
                        </label>
                        <input
                          type="text"
                          name="targetActivity"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Activity name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {suggestionType === 'input' ? 'Source Activity (provides input)' : 'Target Activity (receives output)'} *
                        </label>
                        <input
                          type="text"
                          name="sourceActivity"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Activity name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Source Stakeholder *</label>
                        <select
                          name="sourceStakeholder"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select stakeholder...</option>
                          {stakeholders.map(s => (
                            <option key={s.name} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Source Phase *</label>
                        <select
                          name="sourcePhase"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select phase...</option>
                          {phases.map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Connection Description *</label>
                        <textarea
                          name="connectionDescription"
                          required
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Describe what information or deliverable flows through this connection..."
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name (Optional)</label>
                    <input
                      type="text"
                      name="submitterName"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your name or organization"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Additional Notes (Optional)</label>
                    <textarea
                      name="notes"
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Any additional context or reasoning for this suggestion..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowSuggestionModal(false)}
                      disabled={submitState === 'submitting'}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitState === 'submitting'}
                      className="flex-1 px-4 py-2 rounded text-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#003366' }}
                      onMouseEnter={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = '#002244'; }}
                      onMouseLeave={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = '#003366'; }}
                    >
                      {submitState === 'submitting' ? 'Submitting…' : 'Submit Suggestion'}
                    </button>
                  </div>
                </form>
                </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 text-white text-xs px-4 py-2.5" style={{ backgroundColor: '#003366' }}>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-2">
          <div className="flex flex-col gap-0.5 leading-tight">
            <div>
              © {new Date().getFullYear()} <a href="https://www.becquerelinstitute.eu/" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-300">Becquerel Institute</a>. All rights reserved.
              {' · '}Developed based on the <a href="https://iea-pvps.org/research-tasks/performance-operation-and-reliability-of-photovoltaic-systems/" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-300">IEA PVPS Task 13</a> report <a href="https://iea-pvps.org/key-topics/t13-pv-project-decisions-2026/" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-300">T13-36:2026 "Photovoltaic Project Decisions: Quality, Performance, and Economic Value"</a> and with the support of the <a href="https://supernova-pv.eu/" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-300">SUPERNOVA</a> project.
            </div>
            <div className="opacity-80 text-[10px]">
              SUPERNOVA is co-funded by the European Union under Horizon Europe Grant Agreement No 101146883. Views and opinions expressed are however those of the author(s) only and do not necessarily reflect those of the European Union or CINEA. Neither the European Union nor the granting authority can be held responsible for them.
            </div>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <span className="opacity-80 text-[10px]" title={`Build date ${appVersion.buildDate}`}>
              v{appVersion.version} · {appVersion.buildDate}
            </span>
            <button onClick={() => setShowPrivacyModal(true)} className="underline hover:text-orange-300">Privacy Notice</button>
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      {showPrivacyModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ color: '#003366' }}>Privacy Notice</h3>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="text-sm text-gray-700 space-y-3 leading-relaxed">
              <p>
                This website is operated by Becquerel Institute in support of IEA PVPS Task 13 and the SUPERNOVA project (Horizon Europe Grant Agreement No 101146883).
              </p>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Cookies and tracking</h4>
                <p>
                  This site does not use analytics, advertising, or tracking cookies. No personal data is collected from visitors who simply browse the workflow.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Data you submit</h4>
                <p>
                  If you voluntarily use the suggestion form to propose workflow improvements, the information you enter (including any name, organisation, and notes you choose to provide) is transmitted via Netlify Forms and stored for review by the site operator. This data is used solely to evaluate and improve the workflow, and is not shared with third parties or used for marketing.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Hosting</h4>
                <p>
                  The site is hosted by Netlify. Netlify may process standard server access logs (e.g. IP address, user agent) for security and operational purposes. See Netlify's privacy policy for details.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Your rights</h4>
                <p>
                  Under the EU General Data Protection Regulation (GDPR), you have the right to access, correct, or request deletion of any personal data you have submitted. To exercise these rights, or for any privacy-related questions, please contact Becquerel Institute via{' '}
                  <a href="https://www.becquerelinstitute.eu/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#003366' }}>becquerelinstitute.eu</a>.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Copyright & reuse</h4>
                <p>
                  © {new Date().getFullYear()} Becquerel Institute. Unless otherwise indicated, the content, diagrams, and workflow data on this site are made available under the{' '}
                  <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#003366' }}>Creative Commons Attribution 4.0 International (CC BY 4.0)</a> licence. You are free to share and adapt the material for any purpose, provided that appropriate credit is given to Becquerel Institute and the IEA PVPS Task 13 report T13-36:2026. Third-party materials (logos, photographs, externally referenced figures) may be subject to separate terms.
                </p>
              </div>
              <p className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}
              </p>
            </div>
            <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="px-4 py-2 rounded text-white transition-colors"
                style={{ backgroundColor: '#003366' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#002244'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#003366'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarPVWorkflow;
